import * as Tone from 'tone';
import { logger } from '@/services/logging';
import type { WorldId } from '@/config/worlds';

// Music can play for any world or the lobby/menu screens
export type MusicThemeId = WorldId | 'lobby';

// -- Types ------------------------------------------------------------------

interface MusicNote {
  time: string; // "bars:quarters:sixteenths"
  pitch: string | string[];
  duration: string;
  velocity: number;
}

interface DrumHit {
  time: string;
  drum: 'kick' | 'snare' | 'hihat' | 'tom';
  velocity: number;
  pitch?: string; // optional tuning for kick/tom
}

// SynthOverrides kept for future use if we add modulation synths back
interface SynthOverrides {
  melody?: Record<string, never>;
  pad?: Record<string, never>;
  arp?: Record<string, never>;
}

interface EffectsOverrides {
  filterFrequency?: number;
  reverbDecay?: number;
  reverbWet?: number;
  delayFeedback?: number;
  delayWet?: number;
}

interface WorldTheme {
  tempo: number;
  loopBars: number;
  bass: MusicNote[];
  pad: MusicNote[];
  melody: MusicNote[];
  arp: MusicNote[];
  drums?: DrumHit[];
  synthOverrides?: SynthOverrides;
  effectsOverrides?: EffectsOverrides;
}

// -- World Compositions -----------------------------------------------------
//
// All themes rooted in C (various modes) to harmonize with the existing
// SFX palette. Each is an 8-bar loop designed to feel calm, melodic,
// and non-cliche. The effects chain turns these sparse notes into a
// lush, filtered soundscape.

// -- Greenwood - "Sunny Meadow" ---------------------------------------------
// C Major | 104 BPM
// Bright, bouncy, joyful. Think Animal Crossing meets Kirby -- playful
// staccato melody, rhythmic bass, sparkling arps.
// Progression: C | G/B | Am | F | C | Em | F | G

const GREENWOOD: WorldTheme = {
  tempo: 104,
  loopBars: 8,
  effectsOverrides: {
    filterFrequency: 5000,
    reverbDecay: 2,
    reverbWet: 0.15,
  },
  bass: [
    // Bouncy staccato bass -- drives the groove
    { time: '0:0:0', pitch: 'C2', duration: '8n', velocity: 0.45 },
    { time: '0:1:2', pitch: 'C2', duration: '8n', velocity: 0.35 },
    { time: '0:3:0', pitch: 'C3', duration: '8n', velocity: 0.38 },
    { time: '1:0:0', pitch: 'B1', duration: '8n', velocity: 0.45 },
    { time: '1:1:2', pitch: 'B1', duration: '8n', velocity: 0.35 },
    { time: '1:3:0', pitch: 'G2', duration: '8n', velocity: 0.38 },
    { time: '2:0:0', pitch: 'A1', duration: '8n', velocity: 0.45 },
    { time: '2:1:2', pitch: 'A1', duration: '8n', velocity: 0.35 },
    { time: '2:3:0', pitch: 'A2', duration: '8n', velocity: 0.38 },
    { time: '3:0:0', pitch: 'F2', duration: '8n', velocity: 0.45 },
    { time: '3:1:2', pitch: 'F2', duration: '8n', velocity: 0.35 },
    { time: '3:3:0', pitch: 'F2', duration: '8n', velocity: 0.38 },
    { time: '4:0:0', pitch: 'C2', duration: '8n', velocity: 0.45 },
    { time: '4:1:2', pitch: 'C3', duration: '8n', velocity: 0.35 },
    { time: '4:3:0', pitch: 'C2', duration: '8n', velocity: 0.38 },
    { time: '5:0:0', pitch: 'E2', duration: '8n', velocity: 0.42 },
    { time: '5:1:2', pitch: 'E2', duration: '8n', velocity: 0.35 },
    { time: '5:3:0', pitch: 'G2', duration: '8n', velocity: 0.38 },
    { time: '6:0:0', pitch: 'F2', duration: '8n', velocity: 0.45 },
    { time: '6:1:2', pitch: 'F2', duration: '8n', velocity: 0.35 },
    { time: '6:3:0', pitch: 'A2', duration: '8n', velocity: 0.38 },
    { time: '7:0:0', pitch: 'G1', duration: '8n', velocity: 0.45 },
    { time: '7:1:2', pitch: 'G2', duration: '8n', velocity: 0.35 },
    { time: '7:3:0', pitch: 'G1', duration: '8n', velocity: 0.40 },
  ],
  pad: [
    // Short stabs -- punchy, not washy
    { time: '0:0:0', pitch: ['E3', 'G3', 'C4'], duration: '4n', velocity: 0.25 },
    { time: '0:2:0', pitch: ['E3', 'G3', 'B3'], duration: '4n', velocity: 0.22 },
    { time: '1:0:0', pitch: ['D3', 'G3', 'B3'], duration: '4n', velocity: 0.25 },
    { time: '1:2:0', pitch: ['D3', 'G3', 'B3'], duration: '4n', velocity: 0.22 },
    { time: '2:0:0', pitch: ['C3', 'E3', 'A3'], duration: '4n', velocity: 0.25 },
    { time: '2:2:0', pitch: ['C3', 'E3', 'G3'], duration: '4n', velocity: 0.22 },
    { time: '3:0:0', pitch: ['C3', 'F3', 'A3'], duration: '4n', velocity: 0.25 },
    { time: '3:2:0', pitch: ['C3', 'F3', 'A3'], duration: '4n', velocity: 0.22 },
    { time: '4:0:0', pitch: ['E3', 'G3', 'C4'], duration: '4n', velocity: 0.25 },
    { time: '4:2:0', pitch: ['E3', 'G3', 'C4'], duration: '4n', velocity: 0.22 },
    { time: '5:0:0', pitch: ['E3', 'G3', 'B3'], duration: '4n', velocity: 0.25 },
    { time: '5:2:0', pitch: ['E3', 'G3', 'B3'], duration: '4n', velocity: 0.22 },
    { time: '6:0:0', pitch: ['C3', 'F3', 'A3'], duration: '4n', velocity: 0.25 },
    { time: '6:2:0', pitch: ['C3', 'F3', 'A3'], duration: '4n', velocity: 0.22 },
    { time: '7:0:0', pitch: ['D3', 'G3', 'B3'], duration: '4n', velocity: 0.25 },
    { time: '7:2:0', pitch: ['D3', 'G3', 'B3'], duration: '4n', velocity: 0.22 },
  ],
  melody: [
    // Bouncy, catchy -- staccato with leaps, think game menu music
    // Phrase 1 (bars 0-1): call
    { time: '0:0:0', pitch: 'E5', duration: '8n', velocity: 0.50 },
    { time: '0:0:2', pitch: 'G5', duration: '8n', velocity: 0.45 },
    { time: '0:1:0', pitch: 'A5', duration: '8n', velocity: 0.52 },
    { time: '0:2:0', pitch: 'G5', duration: '8n', velocity: 0.45 },
    { time: '0:2:2', pitch: 'E5', duration: '8n', velocity: 0.42 },
    { time: '0:3:0', pitch: 'D5', duration: '8n', velocity: 0.40 },
    { time: '1:0:0', pitch: 'C5', duration: '4n', velocity: 0.48 },
    // Phrase 2 (bars 2-3): response with leap
    { time: '2:0:0', pitch: 'C5', duration: '8n', velocity: 0.48 },
    { time: '2:0:2', pitch: 'E5', duration: '8n', velocity: 0.45 },
    { time: '2:1:0', pitch: 'A5', duration: '8n', velocity: 0.52 },
    { time: '2:2:0', pitch: 'G5', duration: '4n', velocity: 0.48 },
    { time: '3:0:0', pitch: 'A4', duration: '8n', velocity: 0.42 },
    { time: '3:0:2', pitch: 'B4', duration: '8n', velocity: 0.42 },
    { time: '3:1:0', pitch: 'C5', duration: '4n', velocity: 0.48 },
    // Phrase 3 (bars 4-5): climbs higher, energetic
    { time: '4:0:0', pitch: 'E5', duration: '8n', velocity: 0.50 },
    { time: '4:0:2', pitch: 'G5', duration: '8n', velocity: 0.48 },
    { time: '4:1:0', pitch: 'A5', duration: '8n', velocity: 0.52 },
    { time: '4:1:2', pitch: 'C6', duration: '8n', velocity: 0.55 },
    { time: '4:2:0', pitch: 'A5', duration: '8n', velocity: 0.48 },
    { time: '4:3:0', pitch: 'G5', duration: '8n', velocity: 0.45 },
    { time: '5:0:0', pitch: 'E5', duration: '4n', velocity: 0.48 },
    { time: '5:2:0', pitch: 'D5', duration: '8n', velocity: 0.42 },
    // Phrase 4 (bars 6-7): resolves with bounce
    { time: '6:0:0', pitch: 'C5', duration: '8n', velocity: 0.48 },
    { time: '6:0:2', pitch: 'D5', duration: '8n', velocity: 0.45 },
    { time: '6:1:0', pitch: 'E5', duration: '8n', velocity: 0.50 },
    { time: '6:2:0', pitch: 'G5', duration: '8n', velocity: 0.48 },
    { time: '6:3:0', pitch: 'E5', duration: '8n', velocity: 0.42 },
    { time: '7:0:0', pitch: 'D5', duration: '8n', velocity: 0.45 },
    { time: '7:0:2', pitch: 'C5', duration: '4n', velocity: 0.50 },
  ],
  arp: [
    // Sparkling 16th-note arpeggios -- high register shimmer
    { time: '0:0:0', pitch: 'C6', duration: '16n', velocity: 0.28 },
    { time: '0:0:2', pitch: 'E5', duration: '16n', velocity: 0.25 },
    { time: '0:1:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '0:1:2', pitch: 'C6', duration: '16n', velocity: 0.25 },
    { time: '0:2:0', pitch: 'E5', duration: '16n', velocity: 0.28 },
    { time: '0:2:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '1:0:0', pitch: 'B5', duration: '16n', velocity: 0.28 },
    { time: '1:0:2', pitch: 'D5', duration: '16n', velocity: 0.25 },
    { time: '1:1:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '1:1:2', pitch: 'B5', duration: '16n', velocity: 0.25 },
    { time: '2:0:0', pitch: 'A5', duration: '16n', velocity: 0.28 },
    { time: '2:0:2', pitch: 'C5', duration: '16n', velocity: 0.25 },
    { time: '2:1:0', pitch: 'E5', duration: '16n', velocity: 0.28 },
    { time: '2:1:2', pitch: 'A5', duration: '16n', velocity: 0.25 },
    { time: '3:0:0', pitch: 'A5', duration: '16n', velocity: 0.28 },
    { time: '3:0:2', pitch: 'F5', duration: '16n', velocity: 0.25 },
    { time: '3:1:0', pitch: 'C5', duration: '16n', velocity: 0.28 },
    { time: '3:1:2', pitch: 'A5', duration: '16n', velocity: 0.25 },
    { time: '4:0:0', pitch: 'C6', duration: '16n', velocity: 0.28 },
    { time: '4:0:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '4:1:0', pitch: 'E5', duration: '16n', velocity: 0.28 },
    { time: '4:1:2', pitch: 'C6', duration: '16n', velocity: 0.25 },
    { time: '5:0:0', pitch: 'B5', duration: '16n', velocity: 0.28 },
    { time: '5:0:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '5:1:0', pitch: 'E5', duration: '16n', velocity: 0.28 },
    { time: '5:1:2', pitch: 'B5', duration: '16n', velocity: 0.25 },
    { time: '6:0:0', pitch: 'A5', duration: '16n', velocity: 0.28 },
    { time: '6:0:2', pitch: 'C5', duration: '16n', velocity: 0.25 },
    { time: '6:1:0', pitch: 'F5', duration: '16n', velocity: 0.28 },
    { time: '6:1:2', pitch: 'A5', duration: '16n', velocity: 0.25 },
    { time: '7:0:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '7:0:2', pitch: 'B5', duration: '16n', velocity: 0.25 },
    { time: '7:1:0', pitch: 'D5', duration: '16n', velocity: 0.28 },
    { time: '7:1:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
  ],
};

// -- Greenwood drums: Light hi-hat quarters, soft kick on beat 1 every 2 bars
// Defined separately to keep theme object readable, merged below.
const GREENWOOD_DRUMS: DrumHit[] = [
  // Bar 0: kick + hi-hats
  { time: '0:0:0', drum: 'kick', velocity: 0.25 },
  { time: '0:0:0', drum: 'hihat', velocity: 0.12 },
  { time: '0:1:0', drum: 'hihat', velocity: 0.10 },
  { time: '0:2:0', drum: 'hihat', velocity: 0.12 },
  { time: '0:3:0', drum: 'hihat', velocity: 0.10 },
  // Bar 1: hi-hats only
  { time: '1:0:0', drum: 'hihat', velocity: 0.12 },
  { time: '1:1:0', drum: 'hihat', velocity: 0.10 },
  { time: '1:2:0', drum: 'hihat', velocity: 0.12 },
  { time: '1:3:0', drum: 'hihat', velocity: 0.10 },
  // Bar 2: kick + hi-hats
  { time: '2:0:0', drum: 'kick', velocity: 0.25 },
  { time: '2:0:0', drum: 'hihat', velocity: 0.12 },
  { time: '2:1:0', drum: 'hihat', velocity: 0.10 },
  { time: '2:2:0', drum: 'hihat', velocity: 0.12 },
  { time: '2:3:0', drum: 'hihat', velocity: 0.10 },
  // Bar 3: hi-hats only
  { time: '3:0:0', drum: 'hihat', velocity: 0.12 },
  { time: '3:1:0', drum: 'hihat', velocity: 0.10 },
  { time: '3:2:0', drum: 'hihat', velocity: 0.12 },
  { time: '3:3:0', drum: 'hihat', velocity: 0.10 },
  // Bar 4: kick + hi-hats
  { time: '4:0:0', drum: 'kick', velocity: 0.25 },
  { time: '4:0:0', drum: 'hihat', velocity: 0.12 },
  { time: '4:1:0', drum: 'hihat', velocity: 0.10 },
  { time: '4:2:0', drum: 'hihat', velocity: 0.12 },
  { time: '4:3:0', drum: 'hihat', velocity: 0.10 },
  // Bar 5: hi-hats only
  { time: '5:0:0', drum: 'hihat', velocity: 0.12 },
  { time: '5:1:0', drum: 'hihat', velocity: 0.10 },
  { time: '5:2:0', drum: 'hihat', velocity: 0.12 },
  { time: '5:3:0', drum: 'hihat', velocity: 0.10 },
  // Bar 6: kick + hi-hats
  { time: '6:0:0', drum: 'kick', velocity: 0.25 },
  { time: '6:0:0', drum: 'hihat', velocity: 0.12 },
  { time: '6:1:0', drum: 'hihat', velocity: 0.10 },
  { time: '6:2:0', drum: 'hihat', velocity: 0.12 },
  { time: '6:3:0', drum: 'hihat', velocity: 0.10 },
  // Bar 7: hi-hats only
  { time: '7:0:0', drum: 'hihat', velocity: 0.12 },
  { time: '7:1:0', drum: 'hihat', velocity: 0.10 },
  { time: '7:2:0', drum: 'hihat', velocity: 0.12 },
  { time: '7:3:0', drum: 'hihat', velocity: 0.10 },
];

// Merge drums into Greenwood
GREENWOOD.drums = GREENWOOD_DRUMS;

// -- Stonehollow - "Crystal Cavern" -----------------------------------------
// C minor (Cm Eb G) | 92 BPM
// Mysterious but rhythmic -- bouncy cave exploration, not sad.
// Think Hollow Knight meets chiptune -- driving pulse with crystalline drips.
// Progression: Cm | Eb | Bb | G | Cm | Eb | F | G

const STONEHOLLOW: WorldTheme = {
  tempo: 92,
  loopBars: 8,
  effectsOverrides: {
    filterFrequency: 3800,
    reverbDecay: 2.5,
    reverbWet: 0.22,
    delayFeedback: 0.12,
    delayWet: 0.10,
  },
  bass: [
    // Rhythmic, bouncy bass -- keeps the cave alive
    { time: '0:0:0', pitch: 'C2', duration: '8n', velocity: 0.45 },
    { time: '0:1:2', pitch: 'C2', duration: '8n', velocity: 0.35 },
    { time: '0:3:0', pitch: 'G2', duration: '8n', velocity: 0.38 },
    { time: '1:0:0', pitch: 'Eb2', duration: '8n', velocity: 0.45 },
    { time: '1:1:2', pitch: 'Eb2', duration: '8n', velocity: 0.35 },
    { time: '1:3:0', pitch: 'Bb2', duration: '8n', velocity: 0.38 },
    { time: '2:0:0', pitch: 'Bb1', duration: '8n', velocity: 0.45 },
    { time: '2:1:2', pitch: 'Bb1', duration: '8n', velocity: 0.35 },
    { time: '2:3:0', pitch: 'F2', duration: '8n', velocity: 0.38 },
    { time: '3:0:0', pitch: 'G1', duration: '8n', velocity: 0.45 },
    { time: '3:1:2', pitch: 'G2', duration: '8n', velocity: 0.35 },
    { time: '3:3:0', pitch: 'G1', duration: '8n', velocity: 0.40 },
    { time: '4:0:0', pitch: 'C2', duration: '8n', velocity: 0.45 },
    { time: '4:1:2', pitch: 'C2', duration: '8n', velocity: 0.35 },
    { time: '4:3:0', pitch: 'G2', duration: '8n', velocity: 0.38 },
    { time: '5:0:0', pitch: 'Eb2', duration: '8n', velocity: 0.45 },
    { time: '5:1:2', pitch: 'Eb2', duration: '8n', velocity: 0.35 },
    { time: '5:3:0', pitch: 'Bb2', duration: '8n', velocity: 0.38 },
    { time: '6:0:0', pitch: 'F2', duration: '8n', velocity: 0.45 },
    { time: '6:1:2', pitch: 'F2', duration: '8n', velocity: 0.35 },
    { time: '6:3:0', pitch: 'C2', duration: '8n', velocity: 0.38 },
    { time: '7:0:0', pitch: 'G1', duration: '8n', velocity: 0.45 },
    { time: '7:1:2', pitch: 'G2', duration: '8n', velocity: 0.35 },
    { time: '7:3:0', pitch: 'G1', duration: '8n', velocity: 0.42 },
  ],
  pad: [
    // Short stabs, not wash
    { time: '0:0:0', pitch: ['Eb3', 'G3', 'C4'], duration: '4n', velocity: 0.22 },
    { time: '1:0:0', pitch: ['Eb3', 'G3', 'Bb3'], duration: '4n', velocity: 0.22 },
    { time: '2:0:0', pitch: ['D3', 'F3', 'Bb3'], duration: '4n', velocity: 0.22 },
    { time: '3:0:0', pitch: ['D3', 'G3', 'Bb3'], duration: '4n', velocity: 0.22 },
    { time: '4:0:0', pitch: ['Eb3', 'G3', 'C4'], duration: '4n', velocity: 0.22 },
    { time: '5:0:0', pitch: ['Eb3', 'G3', 'Bb3'], duration: '4n', velocity: 0.22 },
    { time: '6:0:0', pitch: ['C3', 'F3', 'Ab3'], duration: '4n', velocity: 0.22 },
    { time: '7:0:0', pitch: ['D3', 'G3', 'B3'], duration: '4n', velocity: 0.22 },
  ],
  melody: [
    // Bouncy minor melody -- mysterious but energetic
    { time: '0:0:0', pitch: 'G4', duration: '8n', velocity: 0.50 },
    { time: '0:0:2', pitch: 'Bb4', duration: '8n', velocity: 0.45 },
    { time: '0:1:0', pitch: 'C5', duration: '8n', velocity: 0.52 },
    { time: '0:2:0', pitch: 'Bb4', duration: '8n', velocity: 0.45 },
    { time: '0:3:0', pitch: 'G4', duration: '8n', velocity: 0.42 },
    { time: '1:0:0', pitch: 'Eb5', duration: '8n', velocity: 0.52 },
    { time: '1:1:0', pitch: 'D5', duration: '8n', velocity: 0.48 },
    { time: '1:2:0', pitch: 'C5', duration: '4n', velocity: 0.48 },
    { time: '2:0:0', pitch: 'Bb4', duration: '8n', velocity: 0.48 },
    { time: '2:0:2', pitch: 'C5', duration: '8n', velocity: 0.45 },
    { time: '2:1:0', pitch: 'D5', duration: '8n', velocity: 0.50 },
    { time: '2:2:0', pitch: 'Eb5', duration: '8n', velocity: 0.48 },
    { time: '3:0:0', pitch: 'D5', duration: '4n', velocity: 0.50 },
    { time: '3:2:0', pitch: 'B4', duration: '8n', velocity: 0.42 },
    // Second half -- higher energy
    { time: '4:0:0', pitch: 'C5', duration: '8n', velocity: 0.50 },
    { time: '4:0:2', pitch: 'Eb5', duration: '8n', velocity: 0.48 },
    { time: '4:1:0', pitch: 'G5', duration: '8n', velocity: 0.55 },
    { time: '4:2:0', pitch: 'Eb5', duration: '8n', velocity: 0.48 },
    { time: '4:3:0', pitch: 'C5', duration: '8n', velocity: 0.45 },
    { time: '5:0:0', pitch: 'D5', duration: '8n', velocity: 0.48 },
    { time: '5:1:0', pitch: 'Eb5', duration: '4n', velocity: 0.50 },
    { time: '6:0:0', pitch: 'C5', duration: '8n', velocity: 0.48 },
    { time: '6:0:2', pitch: 'Bb4', duration: '8n', velocity: 0.45 },
    { time: '6:1:0', pitch: 'Ab4', duration: '8n', velocity: 0.42 },
    { time: '6:2:0', pitch: 'G4', duration: '8n', velocity: 0.45 },
    { time: '7:0:0', pitch: 'D5', duration: '8n', velocity: 0.48 },
    { time: '7:1:0', pitch: 'C5', duration: '4n', velocity: 0.50 },
  ],
  arp: [
    // Crystalline drip arpeggios -- minor key sparkle
    { time: '0:0:0', pitch: 'C5', duration: '16n', velocity: 0.28 },
    { time: '0:1:0', pitch: 'Eb5', duration: '16n', velocity: 0.25 },
    { time: '0:2:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '0:3:0', pitch: 'C6', duration: '16n', velocity: 0.25 },
    { time: '1:0:0', pitch: 'Bb5', duration: '16n', velocity: 0.28 },
    { time: '1:1:0', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '1:2:0', pitch: 'Eb5', duration: '16n', velocity: 0.28 },
    { time: '2:0:0', pitch: 'Bb5', duration: '16n', velocity: 0.28 },
    { time: '2:1:0', pitch: 'F5', duration: '16n', velocity: 0.25 },
    { time: '2:2:0', pitch: 'D5', duration: '16n', velocity: 0.28 },
    { time: '3:0:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '3:1:0', pitch: 'D5', duration: '16n', velocity: 0.25 },
    { time: '3:2:0', pitch: 'B4', duration: '16n', velocity: 0.28 },
    { time: '4:0:0', pitch: 'C5', duration: '16n', velocity: 0.28 },
    { time: '4:1:0', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '4:2:0', pitch: 'Eb5', duration: '16n', velocity: 0.28 },
    { time: '5:0:0', pitch: 'Bb5', duration: '16n', velocity: 0.28 },
    { time: '5:1:0', pitch: 'Eb5', duration: '16n', velocity: 0.25 },
    { time: '6:0:0', pitch: 'Ab5', duration: '16n', velocity: 0.28 },
    { time: '6:1:0', pitch: 'F5', duration: '16n', velocity: 0.25 },
    { time: '6:2:0', pitch: 'C5', duration: '16n', velocity: 0.28 },
    { time: '7:0:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '7:1:0', pitch: 'B5', duration: '16n', velocity: 0.25 },
    { time: '7:2:0', pitch: 'D5', duration: '16n', velocity: 0.28 },
  ],
};

// Stonehollow drums: Sparse tom hits only (cave drips feel), no kick/snare
const STONEHOLLOW_DRUMS: DrumHit[] = [
  { time: '0:3:0', drum: 'tom', velocity: 0.18, pitch: 'E3' },
  { time: '2:1:2', drum: 'tom', velocity: 0.15, pitch: 'C3' },
  { time: '3:3:0', drum: 'tom', velocity: 0.20, pitch: 'G3' },
  { time: '5:2:0', drum: 'tom', velocity: 0.15, pitch: 'D3' },
  { time: '6:0:2', drum: 'tom', velocity: 0.18, pitch: 'A2' },
  { time: '7:3:2', drum: 'tom', velocity: 0.16, pitch: 'F3' },
];
STONEHOLLOW.drums = STONEHOLLOW_DRUMS;

// -- Frostline - "Sparkle Ice" ---------------------------------------------
// C Lydian (C D E F# G A B) | 96 BPM
// Bright, sparkling, uplifting. Music-box arpeggios with a bouncy melody.
// The F# gives the "floating/magical" Lydian quality. Think Celeste
// meets Mario Galaxy -- crystalline and joyful.
// Progression: C | G | D | Am | C | Em | D | G

const FROSTLINE: WorldTheme = {
  tempo: 96,
  loopBars: 8,
  effectsOverrides: {
    filterFrequency: 5500,
    reverbDecay: 2,
    reverbWet: 0.18,
    delayFeedback: 0.10,
    delayWet: 0.08,
  },
  bass: [
    // Bouncy Lydian bass -- staccato 8ths
    { time: '0:0:0', pitch: 'C2', duration: '8n', velocity: 0.42 },
    { time: '0:1:2', pitch: 'C2', duration: '8n', velocity: 0.32 },
    { time: '0:3:0', pitch: 'E2', duration: '8n', velocity: 0.35 },
    { time: '1:0:0', pitch: 'G1', duration: '8n', velocity: 0.42 },
    { time: '1:1:2', pitch: 'G1', duration: '8n', velocity: 0.32 },
    { time: '1:3:0', pitch: 'B1', duration: '8n', velocity: 0.35 },
    { time: '2:0:0', pitch: 'D2', duration: '8n', velocity: 0.42 },
    { time: '2:1:2', pitch: 'D2', duration: '8n', velocity: 0.32 },
    { time: '2:3:0', pitch: 'F#2', duration: '8n', velocity: 0.35 },
    { time: '3:0:0', pitch: 'A1', duration: '8n', velocity: 0.42 },
    { time: '3:1:2', pitch: 'A1', duration: '8n', velocity: 0.32 },
    { time: '3:3:0', pitch: 'E2', duration: '8n', velocity: 0.35 },
    { time: '4:0:0', pitch: 'C2', duration: '8n', velocity: 0.42 },
    { time: '4:1:2', pitch: 'C2', duration: '8n', velocity: 0.32 },
    { time: '4:3:0', pitch: 'G2', duration: '8n', velocity: 0.35 },
    { time: '5:0:0', pitch: 'E2', duration: '8n', velocity: 0.42 },
    { time: '5:1:2', pitch: 'E2', duration: '8n', velocity: 0.32 },
    { time: '5:3:0', pitch: 'B2', duration: '8n', velocity: 0.35 },
    { time: '6:0:0', pitch: 'D2', duration: '8n', velocity: 0.42 },
    { time: '6:1:2', pitch: 'D2', duration: '8n', velocity: 0.32 },
    { time: '6:3:0', pitch: 'F#2', duration: '8n', velocity: 0.35 },
    { time: '7:0:0', pitch: 'G1', duration: '8n', velocity: 0.42 },
    { time: '7:1:2', pitch: 'G2', duration: '8n', velocity: 0.32 },
    { time: '7:3:0', pitch: 'G1', duration: '8n', velocity: 0.38 },
  ],
  pad: [
    // Short stabs -- Lydian voicings
    { time: '0:0:0', pitch: ['E3', 'G3', 'B3'], duration: '4n', velocity: 0.22 },
    { time: '1:0:0', pitch: ['D3', 'G3', 'B3'], duration: '4n', velocity: 0.22 },
    { time: '2:0:0', pitch: ['F#3', 'A3', 'D4'], duration: '4n', velocity: 0.22 },
    { time: '3:0:0', pitch: ['C3', 'E3', 'A3'], duration: '4n', velocity: 0.22 },
    { time: '4:0:0', pitch: ['E3', 'G3', 'C4'], duration: '4n', velocity: 0.22 },
    { time: '5:0:0', pitch: ['E3', 'G3', 'B3'], duration: '4n', velocity: 0.22 },
    { time: '6:0:0', pitch: ['D3', 'F#3', 'A3'], duration: '4n', velocity: 0.22 },
    { time: '7:0:0', pitch: ['D3', 'G3', 'B3'], duration: '4n', velocity: 0.22 },
  ],
  melody: [
    // Bright, bouncy Lydian melody -- sparkling and uplifting
    { time: '0:0:0', pitch: 'E5', duration: '8n', velocity: 0.50 },
    { time: '0:0:2', pitch: 'G5', duration: '8n', velocity: 0.45 },
    { time: '0:1:0', pitch: 'B5', duration: '8n', velocity: 0.52 },
    { time: '0:2:0', pitch: 'G5', duration: '8n', velocity: 0.45 },
    { time: '0:3:0', pitch: 'E5', duration: '8n', velocity: 0.42 },
    { time: '1:0:0', pitch: 'D5', duration: '8n', velocity: 0.48 },
    { time: '1:1:0', pitch: 'B4', duration: '4n', velocity: 0.45 },
    { time: '2:0:0', pitch: 'F#5', duration: '8n', velocity: 0.52 },
    { time: '2:0:2', pitch: 'A5', duration: '8n', velocity: 0.48 },
    { time: '2:1:0', pitch: 'D5', duration: '8n', velocity: 0.50 },
    { time: '2:2:0', pitch: 'E5', duration: '4n', velocity: 0.48 },
    { time: '3:0:0', pitch: 'C5', duration: '8n', velocity: 0.45 },
    { time: '3:1:0', pitch: 'A4', duration: '4n', velocity: 0.42 },
    // Second half -- climbs to high register
    { time: '4:0:0', pitch: 'G5', duration: '8n', velocity: 0.52 },
    { time: '4:0:2', pitch: 'A5', duration: '8n', velocity: 0.50 },
    { time: '4:1:0', pitch: 'B5', duration: '8n', velocity: 0.55 },
    { time: '4:2:0', pitch: 'G5', duration: '8n', velocity: 0.48 },
    { time: '4:3:0', pitch: 'E5', duration: '8n', velocity: 0.45 },
    { time: '5:0:0', pitch: 'D5', duration: '8n', velocity: 0.48 },
    { time: '5:1:0', pitch: 'E5', duration: '4n', velocity: 0.50 },
    { time: '6:0:0', pitch: 'F#5', duration: '8n', velocity: 0.48 },
    { time: '6:0:2', pitch: 'D5', duration: '8n', velocity: 0.45 },
    { time: '6:1:0', pitch: 'A4', duration: '8n', velocity: 0.42 },
    { time: '6:2:0', pitch: 'B4', duration: '8n', velocity: 0.45 },
    { time: '7:0:0', pitch: 'G4', duration: '8n', velocity: 0.45 },
    { time: '7:0:2', pitch: 'B4', duration: '8n', velocity: 0.42 },
    { time: '7:1:0', pitch: 'C5', duration: '4n', velocity: 0.50 },
  ],
  arp: [
    // Sparkling 16th-note Lydian arps -- crystalline shimmer
    // Bar 0 (C)
    { time: '0:0:0', pitch: 'C6', duration: '16n', velocity: 0.28 },
    { time: '0:0:2', pitch: 'E5', duration: '16n', velocity: 0.25 },
    { time: '0:1:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '0:1:2', pitch: 'B5', duration: '16n', velocity: 0.25 },
    { time: '0:2:0', pitch: 'E5', duration: '16n', velocity: 0.28 },
    { time: '0:2:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    // Bar 1 (G)
    { time: '1:0:0', pitch: 'B5', duration: '16n', velocity: 0.28 },
    { time: '1:0:2', pitch: 'D5', duration: '16n', velocity: 0.25 },
    { time: '1:1:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '1:1:2', pitch: 'B5', duration: '16n', velocity: 0.25 },
    { time: '1:2:0', pitch: 'D5', duration: '16n', velocity: 0.28 },
    { time: '1:2:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    // Bar 2 (D) -- Lydian sparkle with F#
    { time: '2:0:0', pitch: 'D6', duration: '16n', velocity: 0.28 },
    { time: '2:0:2', pitch: 'F#5', duration: '16n', velocity: 0.25 },
    { time: '2:1:0', pitch: 'A5', duration: '16n', velocity: 0.28 },
    { time: '2:1:2', pitch: 'D6', duration: '16n', velocity: 0.25 },
    { time: '2:2:0', pitch: 'F#5', duration: '16n', velocity: 0.28 },
    { time: '2:2:2', pitch: 'A5', duration: '16n', velocity: 0.25 },
    // Bar 3 (Am)
    { time: '3:0:0', pitch: 'A5', duration: '16n', velocity: 0.28 },
    { time: '3:0:2', pitch: 'C5', duration: '16n', velocity: 0.25 },
    { time: '3:1:0', pitch: 'E5', duration: '16n', velocity: 0.28 },
    { time: '3:1:2', pitch: 'A5', duration: '16n', velocity: 0.25 },
    // Bar 4 (C)
    { time: '4:0:0', pitch: 'C6', duration: '16n', velocity: 0.28 },
    { time: '4:0:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '4:1:0', pitch: 'E5', duration: '16n', velocity: 0.28 },
    { time: '4:1:2', pitch: 'C6', duration: '16n', velocity: 0.25 },
    { time: '4:2:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '4:2:2', pitch: 'E5', duration: '16n', velocity: 0.25 },
    // Bar 5 (Em)
    { time: '5:0:0', pitch: 'B5', duration: '16n', velocity: 0.28 },
    { time: '5:0:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '5:1:0', pitch: 'E5', duration: '16n', velocity: 0.28 },
    { time: '5:1:2', pitch: 'B5', duration: '16n', velocity: 0.25 },
    // Bar 6 (D) -- Lydian F#
    { time: '6:0:0', pitch: 'F#5', duration: '16n', velocity: 0.28 },
    { time: '6:0:2', pitch: 'A5', duration: '16n', velocity: 0.25 },
    { time: '6:1:0', pitch: 'D5', duration: '16n', velocity: 0.28 },
    { time: '6:1:2', pitch: 'F#5', duration: '16n', velocity: 0.25 },
    { time: '6:2:0', pitch: 'A5', duration: '16n', velocity: 0.28 },
    { time: '6:2:2', pitch: 'D6', duration: '16n', velocity: 0.25 },
    // Bar 7 (G)
    { time: '7:0:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '7:0:2', pitch: 'B5', duration: '16n', velocity: 0.25 },
    { time: '7:1:0', pitch: 'D5', duration: '16n', velocity: 0.28 },
    { time: '7:1:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '7:2:0', pitch: 'B5', duration: '16n', velocity: 0.28 },
    { time: '7:2:2', pitch: 'D6', duration: '16n', velocity: 0.25 },
  ],
};

// Frostline drums: Soft hi-hat 8ths every other bar, tiny kick on beat 1 every 4 bars
const FROSTLINE_DRUMS: DrumHit[] = [
  // Bar 0: kick + hi-hat 8ths
  { time: '0:0:0', drum: 'kick', velocity: 0.18 },
  { time: '0:0:0', drum: 'hihat', velocity: 0.10 },
  { time: '0:0:2', drum: 'hihat', velocity: 0.08 },
  { time: '0:1:0', drum: 'hihat', velocity: 0.10 },
  { time: '0:1:2', drum: 'hihat', velocity: 0.08 },
  { time: '0:2:0', drum: 'hihat', velocity: 0.10 },
  { time: '0:2:2', drum: 'hihat', velocity: 0.08 },
  { time: '0:3:0', drum: 'hihat', velocity: 0.10 },
  { time: '0:3:2', drum: 'hihat', velocity: 0.08 },
  // Bar 1: rest (no drums)
  // Bar 2: hi-hat 8ths only
  { time: '2:0:0', drum: 'hihat', velocity: 0.10 },
  { time: '2:0:2', drum: 'hihat', velocity: 0.08 },
  { time: '2:1:0', drum: 'hihat', velocity: 0.10 },
  { time: '2:1:2', drum: 'hihat', velocity: 0.08 },
  { time: '2:2:0', drum: 'hihat', velocity: 0.10 },
  { time: '2:2:2', drum: 'hihat', velocity: 0.08 },
  { time: '2:3:0', drum: 'hihat', velocity: 0.10 },
  { time: '2:3:2', drum: 'hihat', velocity: 0.08 },
  // Bar 3: rest
  // Bar 4: kick + hi-hat 8ths
  { time: '4:0:0', drum: 'kick', velocity: 0.18 },
  { time: '4:0:0', drum: 'hihat', velocity: 0.10 },
  { time: '4:0:2', drum: 'hihat', velocity: 0.08 },
  { time: '4:1:0', drum: 'hihat', velocity: 0.10 },
  { time: '4:1:2', drum: 'hihat', velocity: 0.08 },
  { time: '4:2:0', drum: 'hihat', velocity: 0.10 },
  { time: '4:2:2', drum: 'hihat', velocity: 0.08 },
  { time: '4:3:0', drum: 'hihat', velocity: 0.10 },
  { time: '4:3:2', drum: 'hihat', velocity: 0.08 },
  // Bar 5: rest
  // Bar 6: hi-hat 8ths only
  { time: '6:0:0', drum: 'hihat', velocity: 0.10 },
  { time: '6:0:2', drum: 'hihat', velocity: 0.08 },
  { time: '6:1:0', drum: 'hihat', velocity: 0.10 },
  { time: '6:1:2', drum: 'hihat', velocity: 0.08 },
  { time: '6:2:0', drum: 'hihat', velocity: 0.10 },
  { time: '6:2:2', drum: 'hihat', velocity: 0.08 },
  { time: '6:3:0', drum: 'hihat', velocity: 0.10 },
  { time: '6:3:2', drum: 'hihat', velocity: 0.08 },
  // Bar 7: rest
];
FROSTLINE.drums = FROSTLINE_DRUMS;

// -- Emberdeep - "Fire Dance" -----------------------------------------------
// C Dorian (C D Eb F G A Bb) | 108 BPM
// Driving, funky, hot -- the A natural adds groove to the minor feel.
// Think Transistor meets dance music -- head-bobbing rhythm with a fiery
// melody that bounces, not broods.
// Progression: Cm | Bb | F | G | Cm | Eb | F | G

const EMBERDEEP: WorldTheme = {
  tempo: 108,
  loopBars: 8,
  effectsOverrides: {
    filterFrequency: 4200,
    reverbDecay: 1.8,
    reverbWet: 0.14,
  },
  bass: [
    // Driving Dorian bass -- bouncy staccato 8ths with syncopation
    { time: '0:0:0', pitch: 'C2', duration: '8n', velocity: 0.48 },
    { time: '0:1:2', pitch: 'C2', duration: '8n', velocity: 0.38 },
    { time: '0:3:0', pitch: 'G2', duration: '8n', velocity: 0.40 },
    { time: '1:0:0', pitch: 'Bb1', duration: '8n', velocity: 0.48 },
    { time: '1:1:2', pitch: 'Bb1', duration: '8n', velocity: 0.38 },
    { time: '1:3:0', pitch: 'F2', duration: '8n', velocity: 0.40 },
    { time: '2:0:0', pitch: 'F2', duration: '8n', velocity: 0.45 },
    { time: '2:1:2', pitch: 'F2', duration: '8n', velocity: 0.38 },
    { time: '2:3:0', pitch: 'A2', duration: '8n', velocity: 0.40 },
    { time: '3:0:0', pitch: 'G1', duration: '8n', velocity: 0.48 },
    { time: '3:1:2', pitch: 'G2', duration: '8n', velocity: 0.38 },
    { time: '3:3:0', pitch: 'G1', duration: '8n', velocity: 0.42 },
    { time: '4:0:0', pitch: 'C2', duration: '8n', velocity: 0.48 },
    { time: '4:1:2', pitch: 'C2', duration: '8n', velocity: 0.38 },
    { time: '4:3:0', pitch: 'Eb2', duration: '8n', velocity: 0.40 },
    { time: '5:0:0', pitch: 'Eb2', duration: '8n', velocity: 0.45 },
    { time: '5:1:2', pitch: 'Eb2', duration: '8n', velocity: 0.38 },
    { time: '5:3:0', pitch: 'G2', duration: '8n', velocity: 0.40 },
    { time: '6:0:0', pitch: 'F2', duration: '8n', velocity: 0.48 },
    { time: '6:1:2', pitch: 'A2', duration: '8n', velocity: 0.38 },
    { time: '6:3:0', pitch: 'F2', duration: '8n', velocity: 0.40 },
    { time: '7:0:0', pitch: 'G1', duration: '8n', velocity: 0.48 },
    { time: '7:1:2', pitch: 'G2', duration: '8n', velocity: 0.38 },
    { time: '7:3:0', pitch: 'G1', duration: '8n', velocity: 0.42 },
  ],
  pad: [
    // Punchy Dorian stabs -- short, rhythmic
    { time: '0:0:0', pitch: ['Eb3', 'G3', 'C4'], duration: '4n', velocity: 0.24 },
    { time: '0:2:0', pitch: ['Eb3', 'G3', 'Bb3'], duration: '4n', velocity: 0.22 },
    { time: '1:0:0', pitch: ['D3', 'F3', 'Bb3'], duration: '4n', velocity: 0.24 },
    { time: '1:2:0', pitch: ['D3', 'F3', 'Bb3'], duration: '4n', velocity: 0.22 },
    { time: '2:0:0', pitch: ['F3', 'A3', 'C4'], duration: '4n', velocity: 0.24 },
    { time: '2:2:0', pitch: ['F3', 'A3', 'C4'], duration: '4n', velocity: 0.22 },
    { time: '3:0:0', pitch: ['D3', 'G3', 'Bb3'], duration: '4n', velocity: 0.24 },
    { time: '3:2:0', pitch: ['D3', 'G3', 'B3'], duration: '4n', velocity: 0.22 },
    { time: '4:0:0', pitch: ['Eb3', 'G3', 'C4'], duration: '4n', velocity: 0.24 },
    { time: '4:2:0', pitch: ['Eb3', 'G3', 'Bb3'], duration: '4n', velocity: 0.22 },
    { time: '5:0:0', pitch: ['Eb3', 'G3', 'C4'], duration: '4n', velocity: 0.24 },
    { time: '5:2:0', pitch: ['Eb3', 'G3', 'Bb3'], duration: '4n', velocity: 0.22 },
    { time: '6:0:0', pitch: ['D3', 'F3', 'A3'], duration: '4n', velocity: 0.24 },
    { time: '6:2:0', pitch: ['D3', 'F3', 'A3'], duration: '4n', velocity: 0.22 },
    { time: '7:0:0', pitch: ['D3', 'G3', 'B3'], duration: '4n', velocity: 0.24 },
    { time: '7:2:0', pitch: ['D3', 'G3', 'Bb3'], duration: '4n', velocity: 0.22 },
  ],
  melody: [
    // Fiery Dorian melody -- bouncy staccato with attitude
    // Phrase 1 (bars 0-1): punchy call
    { time: '0:0:0', pitch: 'G5', duration: '8n', velocity: 0.52 },
    { time: '0:0:2', pitch: 'A5', duration: '8n', velocity: 0.48 },
    { time: '0:1:0', pitch: 'Bb5', duration: '8n', velocity: 0.55 },
    { time: '0:2:0', pitch: 'A5', duration: '8n', velocity: 0.48 },
    { time: '0:2:2', pitch: 'G5', duration: '8n', velocity: 0.45 },
    { time: '0:3:0', pitch: 'Eb5', duration: '8n', velocity: 0.42 },
    { time: '1:0:0', pitch: 'D5', duration: '4n', velocity: 0.50 },
    // Phrase 2 (bars 2-3): response with Dorian A natural
    { time: '2:0:0', pitch: 'F5', duration: '8n', velocity: 0.48 },
    { time: '2:0:2', pitch: 'A5', duration: '8n', velocity: 0.50 },
    { time: '2:1:0', pitch: 'C6', duration: '8n', velocity: 0.55 },
    { time: '2:2:0', pitch: 'A5', duration: '4n', velocity: 0.48 },
    { time: '3:0:0', pitch: 'G5', duration: '8n', velocity: 0.45 },
    { time: '3:0:2', pitch: 'F5', duration: '8n', velocity: 0.42 },
    { time: '3:1:0', pitch: 'Eb5', duration: '4n', velocity: 0.48 },
    // Phrase 3 (bars 4-5): climbs high, energetic
    { time: '4:0:0', pitch: 'C5', duration: '8n', velocity: 0.50 },
    { time: '4:0:2', pitch: 'Eb5', duration: '8n', velocity: 0.48 },
    { time: '4:1:0', pitch: 'G5', duration: '8n', velocity: 0.52 },
    { time: '4:1:2', pitch: 'Bb5', duration: '8n', velocity: 0.55 },
    { time: '4:2:0', pitch: 'C6', duration: '8n', velocity: 0.58 },
    { time: '4:3:0', pitch: 'Bb5', duration: '8n', velocity: 0.50 },
    { time: '5:0:0', pitch: 'A5', duration: '8n', velocity: 0.48 },
    { time: '5:0:2', pitch: 'G5', duration: '4n', velocity: 0.50 },
    // Phrase 4 (bars 6-7): resolves with groove
    { time: '6:0:0', pitch: 'Eb5', duration: '8n', velocity: 0.48 },
    { time: '6:0:2', pitch: 'F5', duration: '8n', velocity: 0.45 },
    { time: '6:1:0', pitch: 'G5', duration: '8n', velocity: 0.50 },
    { time: '6:2:0', pitch: 'A5', duration: '8n', velocity: 0.48 },
    { time: '6:3:0', pitch: 'G5', duration: '8n', velocity: 0.45 },
    { time: '7:0:0', pitch: 'F5', duration: '8n', velocity: 0.42 },
    { time: '7:0:2', pitch: 'Eb5', duration: '4n', velocity: 0.50 },
  ],
  arp: [
    // Sizzling 16th-note Dorian arps -- fire crackle energy
    // Bar 0 (Cm)
    { time: '0:0:0', pitch: 'C5', duration: '16n', velocity: 0.28 },
    { time: '0:0:2', pitch: 'Eb5', duration: '16n', velocity: 0.25 },
    { time: '0:1:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '0:1:2', pitch: 'Bb5', duration: '16n', velocity: 0.25 },
    { time: '0:2:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '0:2:2', pitch: 'Eb5', duration: '16n', velocity: 0.25 },
    // Bar 1 (Bb)
    { time: '1:0:0', pitch: 'Bb4', duration: '16n', velocity: 0.28 },
    { time: '1:0:2', pitch: 'D5', duration: '16n', velocity: 0.25 },
    { time: '1:1:0', pitch: 'F5', duration: '16n', velocity: 0.28 },
    { time: '1:1:2', pitch: 'Bb5', duration: '16n', velocity: 0.25 },
    // Bar 2 (F)
    { time: '2:0:0', pitch: 'F5', duration: '16n', velocity: 0.28 },
    { time: '2:0:2', pitch: 'A5', duration: '16n', velocity: 0.25 },
    { time: '2:1:0', pitch: 'C6', duration: '16n', velocity: 0.28 },
    { time: '2:1:2', pitch: 'A5', duration: '16n', velocity: 0.25 },
    { time: '2:2:0', pitch: 'F5', duration: '16n', velocity: 0.28 },
    { time: '2:2:2', pitch: 'C6', duration: '16n', velocity: 0.25 },
    // Bar 3 (G)
    { time: '3:0:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '3:0:2', pitch: 'B5', duration: '16n', velocity: 0.25 },
    { time: '3:1:0', pitch: 'D5', duration: '16n', velocity: 0.28 },
    { time: '3:1:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    // Bar 4 (Cm)
    { time: '4:0:0', pitch: 'C6', duration: '16n', velocity: 0.28 },
    { time: '4:0:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '4:1:0', pitch: 'Eb5', duration: '16n', velocity: 0.28 },
    { time: '4:1:2', pitch: 'C5', duration: '16n', velocity: 0.25 },
    { time: '4:2:0', pitch: 'Eb5', duration: '16n', velocity: 0.28 },
    { time: '4:2:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    // Bar 5 (Eb)
    { time: '5:0:0', pitch: 'Eb5', duration: '16n', velocity: 0.28 },
    { time: '5:0:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '5:1:0', pitch: 'Bb5', duration: '16n', velocity: 0.28 },
    { time: '5:1:2', pitch: 'Eb5', duration: '16n', velocity: 0.25 },
    // Bar 6 (F) -- Dorian A natural color
    { time: '6:0:0', pitch: 'F5', duration: '16n', velocity: 0.28 },
    { time: '6:0:2', pitch: 'A5', duration: '16n', velocity: 0.25 },
    { time: '6:1:0', pitch: 'C5', duration: '16n', velocity: 0.28 },
    { time: '6:1:2', pitch: 'F5', duration: '16n', velocity: 0.25 },
    { time: '6:2:0', pitch: 'A5', duration: '16n', velocity: 0.28 },
    { time: '6:2:2', pitch: 'C6', duration: '16n', velocity: 0.25 },
    // Bar 7 (G)
    { time: '7:0:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '7:0:2', pitch: 'B5', duration: '16n', velocity: 0.25 },
    { time: '7:1:0', pitch: 'D6', duration: '16n', velocity: 0.28 },
    { time: '7:1:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '7:2:0', pitch: 'B5', duration: '16n', velocity: 0.28 },
    { time: '7:2:2', pitch: 'D6', duration: '16n', velocity: 0.25 },
  ],
};

// Emberdeep drums: Simple kick pattern with ghost snares (heartbeat drive)
const EMBERDEEP_DRUMS: DrumHit[] = [
  // Bar 0: kick heartbeat + ghost snares
  { time: '0:0:0', drum: 'kick', velocity: 0.35 },
  { time: '0:1:2', drum: 'snare', velocity: 0.12 }, // ghost
  { time: '0:2:0', drum: 'kick', velocity: 0.30 },
  { time: '0:3:2', drum: 'snare', velocity: 0.10 }, // ghost
  // Bar 1
  { time: '1:0:0', drum: 'kick', velocity: 0.35 },
  { time: '1:1:2', drum: 'snare', velocity: 0.12 },
  { time: '1:2:0', drum: 'kick', velocity: 0.30 },
  { time: '1:3:0', drum: 'snare', velocity: 0.15 }, // slightly louder
  // Bar 2
  { time: '2:0:0', drum: 'kick', velocity: 0.35 },
  { time: '2:1:2', drum: 'snare', velocity: 0.12 },
  { time: '2:2:0', drum: 'kick', velocity: 0.30 },
  { time: '2:3:2', drum: 'snare', velocity: 0.10 },
  // Bar 3
  { time: '3:0:0', drum: 'kick', velocity: 0.35 },
  { time: '3:2:0', drum: 'kick', velocity: 0.28 },
  { time: '3:3:0', drum: 'snare', velocity: 0.18 },
  // Bar 4
  { time: '4:0:0', drum: 'kick', velocity: 0.35 },
  { time: '4:1:2', drum: 'snare', velocity: 0.12 },
  { time: '4:2:0', drum: 'kick', velocity: 0.30 },
  { time: '4:3:2', drum: 'snare', velocity: 0.10 },
  // Bar 5
  { time: '5:0:0', drum: 'kick', velocity: 0.35 },
  { time: '5:1:2', drum: 'snare', velocity: 0.12 },
  { time: '5:2:0', drum: 'kick', velocity: 0.30 },
  { time: '5:3:0', drum: 'snare', velocity: 0.15 },
  // Bar 6
  { time: '6:0:0', drum: 'kick', velocity: 0.35 },
  { time: '6:1:2', drum: 'snare', velocity: 0.12 },
  { time: '6:2:0', drum: 'kick', velocity: 0.30 },
  { time: '6:3:2', drum: 'snare', velocity: 0.10 },
  // Bar 7
  { time: '7:0:0', drum: 'kick', velocity: 0.35 },
  { time: '7:2:0', drum: 'kick', velocity: 0.28 },
  { time: '7:3:0', drum: 'snare', velocity: 0.20 },
  { time: '7:3:2', drum: 'snare', velocity: 0.15 },
];
EMBERDEEP.drums = EMBERDEEP_DRUMS;

// -- Arcana - "Mystic Bounce" ----------------------------------------------
// C Mixolydian (C D E F G A Bb) | 100 BPM
// Magical, groovy, mysterious-but-fun. The Bb (b7) gives a bluesy swagger.
// Think FEZ meets funky chiptune -- playful tension with rhythmic drive.
// Progression: C7 | F | Gm | Bb | C | Am | F | G

const ARCANA: WorldTheme = {
  tempo: 100,
  loopBars: 8,
  effectsOverrides: {
    filterFrequency: 4500,
    reverbDecay: 2,
    reverbWet: 0.16,
    delayFeedback: 0.10,
    delayWet: 0.08,
  },
  bass: [
    // Groovy Mixolydian bass -- bouncy 8ths with swagger
    { time: '0:0:0', pitch: 'C2', duration: '8n', velocity: 0.45 },
    { time: '0:1:2', pitch: 'C2', duration: '8n', velocity: 0.35 },
    { time: '0:3:0', pitch: 'E2', duration: '8n', velocity: 0.38 },
    { time: '1:0:0', pitch: 'F2', duration: '8n', velocity: 0.45 },
    { time: '1:1:2', pitch: 'F2', duration: '8n', velocity: 0.35 },
    { time: '1:3:0', pitch: 'A2', duration: '8n', velocity: 0.38 },
    { time: '2:0:0', pitch: 'G1', duration: '8n', velocity: 0.45 },
    { time: '2:1:2', pitch: 'G1', duration: '8n', velocity: 0.35 },
    { time: '2:3:0', pitch: 'Bb1', duration: '8n', velocity: 0.38 },
    { time: '3:0:0', pitch: 'Bb1', duration: '8n', velocity: 0.45 },
    { time: '3:1:2', pitch: 'Bb1', duration: '8n', velocity: 0.35 },
    { time: '3:3:0', pitch: 'F2', duration: '8n', velocity: 0.38 },
    { time: '4:0:0', pitch: 'C2', duration: '8n', velocity: 0.45 },
    { time: '4:1:2', pitch: 'C2', duration: '8n', velocity: 0.35 },
    { time: '4:3:0', pitch: 'G2', duration: '8n', velocity: 0.38 },
    { time: '5:0:0', pitch: 'A1', duration: '8n', velocity: 0.45 },
    { time: '5:1:2', pitch: 'A1', duration: '8n', velocity: 0.35 },
    { time: '5:3:0', pitch: 'E2', duration: '8n', velocity: 0.38 },
    { time: '6:0:0', pitch: 'F2', duration: '8n', velocity: 0.45 },
    { time: '6:1:2', pitch: 'F2', duration: '8n', velocity: 0.35 },
    { time: '6:3:0', pitch: 'A2', duration: '8n', velocity: 0.38 },
    { time: '7:0:0', pitch: 'G1', duration: '8n', velocity: 0.45 },
    { time: '7:1:2', pitch: 'G2', duration: '8n', velocity: 0.35 },
    { time: '7:3:0', pitch: 'G1', duration: '8n', velocity: 0.40 },
  ],
  pad: [
    // Mixolydian stabs -- punchy with Bb color
    { time: '0:0:0', pitch: ['E3', 'G3', 'Bb3'], duration: '4n', velocity: 0.24 },
    { time: '0:2:0', pitch: ['E3', 'G3', 'C4'], duration: '4n', velocity: 0.22 },
    { time: '1:0:0', pitch: ['F3', 'A3', 'C4'], duration: '4n', velocity: 0.24 },
    { time: '1:2:0', pitch: ['F3', 'A3', 'C4'], duration: '4n', velocity: 0.22 },
    { time: '2:0:0', pitch: ['G3', 'Bb3', 'D4'], duration: '4n', velocity: 0.24 },
    { time: '2:2:0', pitch: ['F3', 'Bb3', 'D4'], duration: '4n', velocity: 0.22 },
    { time: '3:0:0', pitch: ['F3', 'Bb3', 'D4'], duration: '4n', velocity: 0.24 },
    { time: '3:2:0', pitch: ['D3', 'F3', 'Bb3'], duration: '4n', velocity: 0.22 },
    { time: '4:0:0', pitch: ['E3', 'G3', 'C4'], duration: '4n', velocity: 0.24 },
    { time: '4:2:0', pitch: ['E3', 'G3', 'Bb3'], duration: '4n', velocity: 0.22 },
    { time: '5:0:0', pitch: ['C3', 'E3', 'A3'], duration: '4n', velocity: 0.24 },
    { time: '5:2:0', pitch: ['C3', 'E3', 'G3'], duration: '4n', velocity: 0.22 },
    { time: '6:0:0', pitch: ['F3', 'A3', 'C4'], duration: '4n', velocity: 0.24 },
    { time: '6:2:0', pitch: ['F3', 'A3', 'C4'], duration: '4n', velocity: 0.22 },
    { time: '7:0:0', pitch: ['D3', 'G3', 'B3'], duration: '4n', velocity: 0.24 },
    { time: '7:2:0', pitch: ['D3', 'G3', 'Bb3'], duration: '4n', velocity: 0.22 },
  ],
  melody: [
    // Playful Mixolydian melody -- bouncy with mystical leaps
    // Phrase 1 (bars 0-1): quirky call with Bb swagger
    { time: '0:0:0', pitch: 'E5', duration: '8n', velocity: 0.50 },
    { time: '0:0:2', pitch: 'G5', duration: '8n', velocity: 0.48 },
    { time: '0:1:0', pitch: 'Bb5', duration: '8n', velocity: 0.52 },
    { time: '0:2:0', pitch: 'A5', duration: '8n', velocity: 0.48 },
    { time: '0:2:2', pitch: 'G5', duration: '8n', velocity: 0.45 },
    { time: '0:3:0', pitch: 'E5', duration: '8n', velocity: 0.42 },
    { time: '1:0:0', pitch: 'F5', duration: '4n', velocity: 0.50 },
    // Phrase 2 (bars 2-3): response with leap
    { time: '2:0:0', pitch: 'G5', duration: '8n', velocity: 0.48 },
    { time: '2:0:2', pitch: 'A5', duration: '8n', velocity: 0.50 },
    { time: '2:1:0', pitch: 'C6', duration: '8n', velocity: 0.55 },
    { time: '2:2:0', pitch: 'Bb5', duration: '4n', velocity: 0.50 },
    { time: '3:0:0', pitch: 'A5', duration: '8n', velocity: 0.45 },
    { time: '3:0:2', pitch: 'G5', duration: '8n', velocity: 0.42 },
    { time: '3:1:0', pitch: 'F5', duration: '4n', velocity: 0.48 },
    // Phrase 3 (bars 4-5): climbs with energy
    { time: '4:0:0', pitch: 'C5', duration: '8n', velocity: 0.48 },
    { time: '4:0:2', pitch: 'E5', duration: '8n', velocity: 0.50 },
    { time: '4:1:0', pitch: 'G5', duration: '8n', velocity: 0.52 },
    { time: '4:1:2', pitch: 'Bb5', duration: '8n', velocity: 0.55 },
    { time: '4:2:0', pitch: 'C6', duration: '8n', velocity: 0.58 },
    { time: '4:3:0', pitch: 'A5', duration: '8n', velocity: 0.50 },
    { time: '5:0:0', pitch: 'G5', duration: '8n', velocity: 0.48 },
    { time: '5:0:2', pitch: 'E5', duration: '4n', velocity: 0.50 },
    // Phrase 4 (bars 6-7): resolves with Bb tension
    { time: '6:0:0', pitch: 'F5', duration: '8n', velocity: 0.48 },
    { time: '6:0:2', pitch: 'G5', duration: '8n', velocity: 0.45 },
    { time: '6:1:0', pitch: 'A5', duration: '8n', velocity: 0.50 },
    { time: '6:2:0', pitch: 'G5', duration: '8n', velocity: 0.48 },
    { time: '6:3:0', pitch: 'E5', duration: '8n', velocity: 0.42 },
    { time: '7:0:0', pitch: 'D5', duration: '8n', velocity: 0.45 },
    { time: '7:0:2', pitch: 'C5', duration: '4n', velocity: 0.50 },
  ],
  arp: [
    // Mystical 16th-note arps -- Mixolydian shimmer with Bb color
    // Bar 0 (C7)
    { time: '0:0:0', pitch: 'C6', duration: '16n', velocity: 0.28 },
    { time: '0:0:2', pitch: 'E5', duration: '16n', velocity: 0.25 },
    { time: '0:1:0', pitch: 'Bb5', duration: '16n', velocity: 0.28 },
    { time: '0:1:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '0:2:0', pitch: 'E5', duration: '16n', velocity: 0.28 },
    { time: '0:2:2', pitch: 'Bb5', duration: '16n', velocity: 0.25 },
    // Bar 1 (F)
    { time: '1:0:0', pitch: 'A5', duration: '16n', velocity: 0.28 },
    { time: '1:0:2', pitch: 'F5', duration: '16n', velocity: 0.25 },
    { time: '1:1:0', pitch: 'C5', duration: '16n', velocity: 0.28 },
    { time: '1:1:2', pitch: 'A5', duration: '16n', velocity: 0.25 },
    // Bar 2 (Gm)
    { time: '2:0:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '2:0:2', pitch: 'Bb5', duration: '16n', velocity: 0.25 },
    { time: '2:1:0', pitch: 'D5', duration: '16n', velocity: 0.28 },
    { time: '2:1:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '2:2:0', pitch: 'Bb5', duration: '16n', velocity: 0.28 },
    { time: '2:2:2', pitch: 'D6', duration: '16n', velocity: 0.25 },
    // Bar 3 (Bb)
    { time: '3:0:0', pitch: 'Bb5', duration: '16n', velocity: 0.28 },
    { time: '3:0:2', pitch: 'F5', duration: '16n', velocity: 0.25 },
    { time: '3:1:0', pitch: 'D5', duration: '16n', velocity: 0.28 },
    { time: '3:1:2', pitch: 'Bb5', duration: '16n', velocity: 0.25 },
    // Bar 4 (C)
    { time: '4:0:0', pitch: 'C6', duration: '16n', velocity: 0.28 },
    { time: '4:0:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '4:1:0', pitch: 'E5', duration: '16n', velocity: 0.28 },
    { time: '4:1:2', pitch: 'C6', duration: '16n', velocity: 0.25 },
    { time: '4:2:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '4:2:2', pitch: 'E5', duration: '16n', velocity: 0.25 },
    // Bar 5 (Am)
    { time: '5:0:0', pitch: 'A5', duration: '16n', velocity: 0.28 },
    { time: '5:0:2', pitch: 'C5', duration: '16n', velocity: 0.25 },
    { time: '5:1:0', pitch: 'E5', duration: '16n', velocity: 0.28 },
    { time: '5:1:2', pitch: 'A5', duration: '16n', velocity: 0.25 },
    // Bar 6 (F)
    { time: '6:0:0', pitch: 'F5', duration: '16n', velocity: 0.28 },
    { time: '6:0:2', pitch: 'A5', duration: '16n', velocity: 0.25 },
    { time: '6:1:0', pitch: 'C5', duration: '16n', velocity: 0.28 },
    { time: '6:1:2', pitch: 'F5', duration: '16n', velocity: 0.25 },
    { time: '6:2:0', pitch: 'A5', duration: '16n', velocity: 0.28 },
    { time: '6:2:2', pitch: 'C6', duration: '16n', velocity: 0.25 },
    // Bar 7 (G)
    { time: '7:0:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '7:0:2', pitch: 'B5', duration: '16n', velocity: 0.25 },
    { time: '7:1:0', pitch: 'D5', duration: '16n', velocity: 0.28 },
    { time: '7:1:2', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '7:2:0', pitch: 'B5', duration: '16n', velocity: 0.28 },
    { time: '7:2:2', pitch: 'D6', duration: '16n', velocity: 0.25 },
  ],
};

// Arcana drums: Irregular tom + kick at unexpected positions (mystical pulse)
const ARCANA_DRUMS: DrumHit[] = [
  { time: '0:1:2', drum: 'kick', velocity: 0.25 },
  { time: '1:0:0', drum: 'tom', velocity: 0.20, pitch: 'D3' },
  { time: '1:3:0', drum: 'kick', velocity: 0.22 },
  { time: '2:2:2', drum: 'tom', velocity: 0.18, pitch: 'G3' },
  { time: '3:1:0', drum: 'kick', velocity: 0.25 },
  { time: '3:3:2', drum: 'tom', velocity: 0.22, pitch: 'E3' },
  { time: '4:0:2', drum: 'tom', velocity: 0.18, pitch: 'A2' },
  { time: '4:3:0', drum: 'kick', velocity: 0.25 },
  { time: '5:2:0', drum: 'tom', velocity: 0.20, pitch: 'F3' },
  { time: '6:1:2', drum: 'kick', velocity: 0.22 },
  { time: '6:3:0', drum: 'tom', velocity: 0.18, pitch: 'C3' },
  { time: '7:0:2', drum: 'tom', velocity: 0.20, pitch: 'Bb2' },
  { time: '7:2:2', drum: 'kick', velocity: 0.25 },
];
ARCANA.drums = ARCANA_DRUMS;

// -- Lobby - "Let's Go!" ---------------------------------------------------
// C Major | 100 BPM
// Energetic, upbeat title screen theme. Bouncy bass, driving arp, and
// a catchy melody that makes you want to hit Play immediately. Think
// Celeste title screen meets Kirby -- bright, rhythmic, fun.
// Progression: C | G/B | Am | F | C | G | F | G

const LOBBY: WorldTheme = {
  tempo: 100,
  loopBars: 8,
  effectsOverrides: {
    filterFrequency: 3800,
    reverbDecay: 2.5,
    reverbWet: 0.15,
    delayFeedback: 0.12,
    delayWet: 0.05,
  },
  bass: [
    // Bouncy, syncopated bass -- drives the groove
    // Bar 0 (C)
    { time: '0:0:0', pitch: 'C2', duration: '8n', velocity: 0.35 },
    { time: '0:1:2', pitch: 'C2', duration: '8n', velocity: 0.28 },
    { time: '0:2:2', pitch: 'C3', duration: '8n', velocity: 0.30 },
    // Bar 1 (G/B)
    { time: '1:0:0', pitch: 'B1', duration: '8n', velocity: 0.35 },
    { time: '1:1:2', pitch: 'B1', duration: '8n', velocity: 0.28 },
    { time: '1:2:2', pitch: 'G2', duration: '8n', velocity: 0.30 },
    // Bar 2 (Am)
    { time: '2:0:0', pitch: 'A1', duration: '8n', velocity: 0.35 },
    { time: '2:1:2', pitch: 'A1', duration: '8n', velocity: 0.28 },
    { time: '2:2:2', pitch: 'A2', duration: '8n', velocity: 0.30 },
    // Bar 3 (F)
    { time: '3:0:0', pitch: 'F2', duration: '8n', velocity: 0.35 },
    { time: '3:1:2', pitch: 'F2', duration: '8n', velocity: 0.28 },
    { time: '3:3:0', pitch: 'F2', duration: '8n', velocity: 0.30 },
    // Bar 4 (C)
    { time: '4:0:0', pitch: 'C2', duration: '8n', velocity: 0.35 },
    { time: '4:1:2', pitch: 'C3', duration: '8n', velocity: 0.28 },
    { time: '4:3:0', pitch: 'C2', duration: '8n', velocity: 0.30 },
    // Bar 5 (G)
    { time: '5:0:0', pitch: 'G1', duration: '8n', velocity: 0.35 },
    { time: '5:1:2', pitch: 'G1', duration: '8n', velocity: 0.28 },
    { time: '5:2:2', pitch: 'G2', duration: '8n', velocity: 0.30 },
    // Bar 6 (F)
    { time: '6:0:0', pitch: 'F2', duration: '8n', velocity: 0.35 },
    { time: '6:1:2', pitch: 'F2', duration: '8n', velocity: 0.28 },
    { time: '6:2:2', pitch: 'F2', duration: '8n', velocity: 0.30 },
    // Bar 7 (G)
    { time: '7:0:0', pitch: 'G1', duration: '8n', velocity: 0.35 },
    { time: '7:1:2', pitch: 'G2', duration: '8n', velocity: 0.28 },
    { time: '7:3:0', pitch: 'G1', duration: '8n', velocity: 0.32 },
  ],
  pad: [
    // Punchier stabs -- shorter durations, 7th extensions
    { time: '0:0:0', pitch: ['E3', 'G3', 'B3', 'D4'], duration: '4n', velocity: 0.22 },
    { time: '0:2:0', pitch: ['E3', 'G3', 'C4'], duration: '4n', velocity: 0.18 },
    { time: '1:0:0', pitch: ['D3', 'G3', 'B3'], duration: '4n', velocity: 0.22 },
    { time: '1:2:0', pitch: ['D3', 'F3', 'B3'], duration: '4n', velocity: 0.18 },
    { time: '2:0:0', pitch: ['C3', 'E3', 'A3', 'B3'], duration: '4n', velocity: 0.21 },
    { time: '2:2:0', pitch: ['C3', 'E3', 'G3'], duration: '4n', velocity: 0.18 },
    { time: '3:0:0', pitch: ['C3', 'F3', 'A3', 'E4'], duration: '4n', velocity: 0.22 },
    { time: '3:2:0', pitch: ['C3', 'F3', 'A3'], duration: '4n', velocity: 0.18 },
    { time: '4:0:0', pitch: ['E3', 'G3', 'B3', 'D4'], duration: '4n', velocity: 0.22 },
    { time: '4:2:0', pitch: ['E3', 'G3', 'C4'], duration: '4n', velocity: 0.18 },
    { time: '5:0:0', pitch: ['D3', 'G3', 'B3', 'F4'], duration: '4n', velocity: 0.22 },
    { time: '5:2:0', pitch: ['D3', 'G3', 'B3'], duration: '4n', velocity: 0.18 },
    { time: '6:0:0', pitch: ['C3', 'F3', 'A3', 'E4'], duration: '4n', velocity: 0.21 },
    { time: '6:2:0', pitch: ['C3', 'F3', 'A3'], duration: '4n', velocity: 0.18 },
    { time: '7:0:0', pitch: ['D3', 'G3', 'B3', 'F4'], duration: '4n', velocity: 0.22 },
    { time: '7:2:0', pitch: ['D3', 'G3', 'B3'], duration: '4n', velocity: 0.18 },
  ],
  melody: [
    // Catchier with octave leaps, rests, call-and-response phrasing
    // Phrase 1 -- upbeat call with leap (bars 0-1)
    { time: '0:0:0', pitch: 'E5', duration: '8n', velocity: 0.38 },
    { time: '0:0:2', pitch: 'G5', duration: '8n', velocity: 0.35 },
    { time: '0:1:0', pitch: 'A5', duration: '4n', velocity: 0.40 },
    // Rest -- breathing room
    { time: '0:3:0', pitch: 'E5', duration: '8n', velocity: 0.33 },
    { time: '0:3:2', pitch: 'D5', duration: '8n', velocity: 0.33 },
    { time: '1:0:0', pitch: 'C5', duration: '4n', velocity: 0.38 },
    // Rest bar 1 beats 1-3 -- let it breathe
    // Phrase 2 -- response with octave leap (bars 2-3)
    { time: '2:0:0', pitch: 'C4', duration: '8n', velocity: 0.33 }, // octave drop
    { time: '2:0:2', pitch: 'E4', duration: '8n', velocity: 0.33 },
    { time: '2:1:0', pitch: 'C5', duration: '4n', velocity: 0.38 }, // octave leap up
    { time: '2:2:0', pitch: 'D5', duration: '8n', velocity: 0.35 },
    { time: '2:3:0', pitch: 'E5', duration: '4n', velocity: 0.38 },
    // Bar 3 -- rest then pickup
    { time: '3:2:0', pitch: 'A4', duration: '8n', velocity: 0.33 },
    { time: '3:2:2', pitch: 'B4', duration: '8n', velocity: 0.33 },
    { time: '3:3:0', pitch: 'C5', duration: '8n', velocity: 0.35 },
    // Phrase 3 -- climbs with energy + leap (bars 4-5)
    { time: '4:0:0', pitch: 'E5', duration: '8n', velocity: 0.38 },
    { time: '4:0:2', pitch: 'G5', duration: '8n', velocity: 0.38 },
    { time: '4:1:0', pitch: 'A5', duration: '4n.', velocity: 0.42 },
    // Rest
    { time: '4:3:0', pitch: 'C6', duration: '8n', velocity: 0.40 }, // high C leap
    { time: '4:3:2', pitch: 'A5', duration: '8n', velocity: 0.38 },
    { time: '5:0:0', pitch: 'G5', duration: '4n', velocity: 0.40 },
    { time: '5:1:0', pitch: 'E5', duration: '4n', velocity: 0.35 },
    // Rest bar 5 beats 2-3
    // Phrase 4 -- resolves with syncopation (bars 6-7)
    { time: '6:0:0', pitch: 'C5', duration: '8n', velocity: 0.35 },
    { time: '6:0:2', pitch: 'D5', duration: '8n', velocity: 0.35 },
    { time: '6:1:0', pitch: 'E5', duration: '4n.', velocity: 0.38 },
    // Rest
    { time: '6:3:0', pitch: 'D5', duration: '8n', velocity: 0.33 },
    { time: '7:0:0', pitch: 'C5', duration: '8n', velocity: 0.35 },
    { time: '7:0:2', pitch: 'E5', duration: '8n', velocity: 0.35 },
    { time: '7:1:0', pitch: 'G5', duration: '2n', velocity: 0.40 },
  ],
  arp: [
    // Driving 16th-note arp pattern -- percussive energy
    // Bar 0 (C)
    { time: '0:0:0', pitch: 'C5', duration: '16n', velocity: 0.25 },
    { time: '0:0:2', pitch: 'E5', duration: '16n', velocity: 0.22 },
    { time: '0:1:0', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '0:1:2', pitch: 'E5', duration: '16n', velocity: 0.22 },
    { time: '0:2:0', pitch: 'C5', duration: '16n', velocity: 0.25 },
    { time: '0:2:2', pitch: 'G5', duration: '16n', velocity: 0.22 },
    { time: '0:3:0', pitch: 'E5', duration: '16n', velocity: 0.25 },
    { time: '0:3:2', pitch: 'C5', duration: '16n', velocity: 0.22 },
    // Bar 1 (G/B)
    { time: '1:0:0', pitch: 'B4', duration: '16n', velocity: 0.25 },
    { time: '1:0:2', pitch: 'D5', duration: '16n', velocity: 0.22 },
    { time: '1:1:0', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '1:1:2', pitch: 'D5', duration: '16n', velocity: 0.22 },
    { time: '1:2:0', pitch: 'B4', duration: '16n', velocity: 0.25 },
    { time: '1:2:2', pitch: 'G5', duration: '16n', velocity: 0.22 },
    { time: '1:3:0', pitch: 'D5', duration: '16n', velocity: 0.25 },
    { time: '1:3:2', pitch: 'B4', duration: '16n', velocity: 0.22 },
    // Bar 2 (Am)
    { time: '2:0:0', pitch: 'A4', duration: '16n', velocity: 0.25 },
    { time: '2:0:2', pitch: 'C5', duration: '16n', velocity: 0.22 },
    { time: '2:1:0', pitch: 'E5', duration: '16n', velocity: 0.25 },
    { time: '2:1:2', pitch: 'C5', duration: '16n', velocity: 0.22 },
    { time: '2:2:0', pitch: 'A4', duration: '16n', velocity: 0.25 },
    { time: '2:2:2', pitch: 'E5', duration: '16n', velocity: 0.22 },
    { time: '2:3:0', pitch: 'C5', duration: '16n', velocity: 0.25 },
    { time: '2:3:2', pitch: 'A4', duration: '16n', velocity: 0.22 },
    // Bar 3 (F)
    { time: '3:0:0', pitch: 'F4', duration: '16n', velocity: 0.25 },
    { time: '3:0:2', pitch: 'A4', duration: '16n', velocity: 0.22 },
    { time: '3:1:0', pitch: 'C5', duration: '16n', velocity: 0.25 },
    { time: '3:1:2', pitch: 'A4', duration: '16n', velocity: 0.22 },
    { time: '3:2:0', pitch: 'F4', duration: '16n', velocity: 0.25 },
    { time: '3:2:2', pitch: 'C5', duration: '16n', velocity: 0.22 },
    { time: '3:3:0', pitch: 'A4', duration: '16n', velocity: 0.25 },
    { time: '3:3:2', pitch: 'F4', duration: '16n', velocity: 0.22 },
    // Bar 4 (C) -- variation
    { time: '4:0:0', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '4:0:2', pitch: 'E5', duration: '16n', velocity: 0.22 },
    { time: '4:1:0', pitch: 'C5', duration: '16n', velocity: 0.25 },
    { time: '4:1:2', pitch: 'E5', duration: '16n', velocity: 0.22 },
    { time: '4:2:0', pitch: 'G5', duration: '16n', velocity: 0.25 },
    { time: '4:2:2', pitch: 'C5', duration: '16n', velocity: 0.22 },
    { time: '4:3:0', pitch: 'E5', duration: '16n', velocity: 0.25 },
    { time: '4:3:2', pitch: 'G5', duration: '16n', velocity: 0.22 },
    // Bar 5 (G)
    { time: '5:0:0', pitch: 'G4', duration: '16n', velocity: 0.25 },
    { time: '5:0:2', pitch: 'B4', duration: '16n', velocity: 0.22 },
    { time: '5:1:0', pitch: 'D5', duration: '16n', velocity: 0.25 },
    { time: '5:1:2', pitch: 'B4', duration: '16n', velocity: 0.22 },
    { time: '5:2:0', pitch: 'G4', duration: '16n', velocity: 0.25 },
    { time: '5:2:2', pitch: 'D5', duration: '16n', velocity: 0.22 },
    { time: '5:3:0', pitch: 'B4', duration: '16n', velocity: 0.25 },
    { time: '5:3:2', pitch: 'G4', duration: '16n', velocity: 0.22 },
    // Bar 6 (F)
    { time: '6:0:0', pitch: 'F4', duration: '16n', velocity: 0.25 },
    { time: '6:0:2', pitch: 'A4', duration: '16n', velocity: 0.22 },
    { time: '6:1:0', pitch: 'C5', duration: '16n', velocity: 0.25 },
    { time: '6:1:2', pitch: 'F5', duration: '16n', velocity: 0.22 },
    { time: '6:2:0', pitch: 'C5', duration: '16n', velocity: 0.25 },
    { time: '6:2:2', pitch: 'A4', duration: '16n', velocity: 0.22 },
    { time: '6:3:0', pitch: 'F4', duration: '16n', velocity: 0.25 },
    { time: '6:3:2', pitch: 'A4', duration: '16n', velocity: 0.22 },
    // Bar 7 (G) -- builds to loop point
    { time: '7:0:0', pitch: 'G4', duration: '16n', velocity: 0.25 },
    { time: '7:0:2', pitch: 'B4', duration: '16n', velocity: 0.22 },
    { time: '7:1:0', pitch: 'D5', duration: '16n', velocity: 0.25 },
    { time: '7:1:2', pitch: 'G5', duration: '16n', velocity: 0.22 },
    { time: '7:2:0', pitch: 'D5', duration: '16n', velocity: 0.25 },
    { time: '7:2:2', pitch: 'B4', duration: '16n', velocity: 0.22 },
    { time: '7:3:0', pitch: 'G5', duration: '16n', velocity: 0.28 },
    { time: '7:3:2', pitch: 'D5', duration: '16n', velocity: 0.25 },
  ],
  drums: [
    // Driving beat: kick on 1 & 3 with syncopation, snare on 2 & 4,
    // steady 8th-note hi-hats, tom fills every 4 bars
    // --- Bar 0 ---
    { time: '0:0:0', drum: 'kick', velocity: 0.50 },
    { time: '0:0:0', drum: 'hihat', velocity: 0.22 },
    { time: '0:0:2', drum: 'hihat', velocity: 0.15 },
    { time: '0:1:0', drum: 'snare', velocity: 0.35 },
    { time: '0:1:0', drum: 'hihat', velocity: 0.20 },
    { time: '0:1:2', drum: 'hihat', velocity: 0.15 },
    { time: '0:2:0', drum: 'kick', velocity: 0.45 },
    { time: '0:2:0', drum: 'hihat', velocity: 0.22 },
    { time: '0:2:2', drum: 'hihat', velocity: 0.15 },
    { time: '0:3:0', drum: 'snare', velocity: 0.35 },
    { time: '0:3:0', drum: 'hihat', velocity: 0.20 },
    { time: '0:3:2', drum: 'kick', velocity: 0.30 },
    // --- Bar 1 ---
    { time: '1:0:0', drum: 'kick', velocity: 0.50 },
    { time: '1:0:0', drum: 'hihat', velocity: 0.22 },
    { time: '1:0:2', drum: 'hihat', velocity: 0.15 },
    { time: '1:1:0', drum: 'snare', velocity: 0.35 },
    { time: '1:1:0', drum: 'hihat', velocity: 0.20 },
    { time: '1:1:2', drum: 'hihat', velocity: 0.15 },
    { time: '1:2:0', drum: 'kick', velocity: 0.45 },
    { time: '1:2:0', drum: 'hihat', velocity: 0.22 },
    { time: '1:2:2', drum: 'hihat', velocity: 0.15 },
    { time: '1:3:0', drum: 'snare', velocity: 0.35 },
    { time: '1:3:0', drum: 'hihat', velocity: 0.20 },
    { time: '1:3:2', drum: 'hihat', velocity: 0.15 },
    // --- Bar 2 ---
    { time: '2:0:0', drum: 'kick', velocity: 0.50 },
    { time: '2:0:0', drum: 'hihat', velocity: 0.22 },
    { time: '2:0:2', drum: 'hihat', velocity: 0.15 },
    { time: '2:1:0', drum: 'snare', velocity: 0.35 },
    { time: '2:1:0', drum: 'hihat', velocity: 0.20 },
    { time: '2:1:2', drum: 'hihat', velocity: 0.15 },
    { time: '2:2:0', drum: 'kick', velocity: 0.45 },
    { time: '2:2:0', drum: 'hihat', velocity: 0.22 },
    { time: '2:2:2', drum: 'kick', velocity: 0.30 },
    { time: '2:3:0', drum: 'snare', velocity: 0.38 },
    { time: '2:3:0', drum: 'hihat', velocity: 0.20 },
    { time: '2:3:2', drum: 'hihat', velocity: 0.15 },
    // --- Bar 3 (tom fill) ---
    { time: '3:0:0', drum: 'kick', velocity: 0.50 },
    { time: '3:0:0', drum: 'hihat', velocity: 0.22 },
    { time: '3:0:2', drum: 'hihat', velocity: 0.15 },
    { time: '3:1:0', drum: 'snare', velocity: 0.35 },
    { time: '3:1:0', drum: 'hihat', velocity: 0.20 },
    { time: '3:1:2', drum: 'hihat', velocity: 0.15 },
    { time: '3:2:0', drum: 'tom', velocity: 0.40, pitch: 'G3' },
    { time: '3:2:2', drum: 'tom', velocity: 0.35, pitch: 'E3' },
    { time: '3:3:0', drum: 'tom', velocity: 0.38, pitch: 'D3' },
    { time: '3:3:2', drum: 'tom', velocity: 0.42, pitch: 'C3' },
    // --- Bar 4 ---
    { time: '4:0:0', drum: 'kick', velocity: 0.52 },
    { time: '4:0:0', drum: 'hihat', velocity: 0.22 },
    { time: '4:0:2', drum: 'hihat', velocity: 0.15 },
    { time: '4:1:0', drum: 'snare', velocity: 0.35 },
    { time: '4:1:0', drum: 'hihat', velocity: 0.20 },
    { time: '4:1:2', drum: 'hihat', velocity: 0.15 },
    { time: '4:2:0', drum: 'kick', velocity: 0.45 },
    { time: '4:2:0', drum: 'hihat', velocity: 0.22 },
    { time: '4:2:2', drum: 'hihat', velocity: 0.15 },
    { time: '4:3:0', drum: 'snare', velocity: 0.35 },
    { time: '4:3:0', drum: 'hihat', velocity: 0.20 },
    { time: '4:3:2', drum: 'kick', velocity: 0.30 },
    // --- Bar 5 ---
    { time: '5:0:0', drum: 'kick', velocity: 0.50 },
    { time: '5:0:0', drum: 'hihat', velocity: 0.22 },
    { time: '5:0:2', drum: 'hihat', velocity: 0.15 },
    { time: '5:1:0', drum: 'snare', velocity: 0.35 },
    { time: '5:1:0', drum: 'hihat', velocity: 0.20 },
    { time: '5:1:2', drum: 'hihat', velocity: 0.15 },
    { time: '5:2:0', drum: 'kick', velocity: 0.45 },
    { time: '5:2:0', drum: 'hihat', velocity: 0.22 },
    { time: '5:2:2', drum: 'hihat', velocity: 0.15 },
    { time: '5:3:0', drum: 'snare', velocity: 0.35 },
    { time: '5:3:0', drum: 'hihat', velocity: 0.20 },
    { time: '5:3:2', drum: 'hihat', velocity: 0.15 },
    // --- Bar 6 ---
    { time: '6:0:0', drum: 'kick', velocity: 0.50 },
    { time: '6:0:0', drum: 'hihat', velocity: 0.22 },
    { time: '6:0:2', drum: 'hihat', velocity: 0.15 },
    { time: '6:1:0', drum: 'snare', velocity: 0.35 },
    { time: '6:1:0', drum: 'hihat', velocity: 0.20 },
    { time: '6:1:2', drum: 'hihat', velocity: 0.15 },
    { time: '6:2:0', drum: 'kick', velocity: 0.45 },
    { time: '6:2:0', drum: 'hihat', velocity: 0.22 },
    { time: '6:2:2', drum: 'kick', velocity: 0.30 },
    { time: '6:3:0', drum: 'snare', velocity: 0.38 },
    { time: '6:3:0', drum: 'hihat', velocity: 0.20 },
    { time: '6:3:2', drum: 'hihat', velocity: 0.15 },
    // --- Bar 7 (build fill) ---
    { time: '7:0:0', drum: 'kick', velocity: 0.52 },
    { time: '7:0:0', drum: 'hihat', velocity: 0.22 },
    { time: '7:0:2', drum: 'hihat', velocity: 0.15 },
    { time: '7:1:0', drum: 'snare', velocity: 0.35 },
    { time: '7:1:0', drum: 'hihat', velocity: 0.20 },
    { time: '7:1:2', drum: 'snare', velocity: 0.28 },
    { time: '7:2:0', drum: 'tom', velocity: 0.42, pitch: 'A3' },
    { time: '7:2:2', drum: 'tom', velocity: 0.38, pitch: 'G3' },
    { time: '7:3:0', drum: 'tom', velocity: 0.40, pitch: 'E3' },
    { time: '7:3:2', drum: 'snare', velocity: 0.45 },
  ],
};

const MUSIC_THEMES: Record<MusicThemeId, WorldTheme> = {
  lobby: LOBBY,
  greenwood: GREENWOOD,
  stonehollow: STONEHOLLOW,
  frostline: FROSTLINE,
  emberdeep: EMBERDEEP,
  arcana: ARCANA,
};

// -- Background Music Player ------------------------------------------------

const FADE_DURATION = 2; // seconds (shortened to prevent lingering voices)
const CROSSFADE_OVERLAP = 1.5; // start new theme before old finishes fading

// Transition stinger -- a soft 3-note motif played between world switches
// Uses lower octave and gentle velocities to stay ambient
const TRANSITION_STINGER: MusicNote[] = [
  { time: '0:0:0', pitch: 'C4', duration: '8n', velocity: 0.20 },
  { time: '0:0:2', pitch: 'E4', duration: '8n', velocity: 0.18 },
  { time: '0:1:0', pitch: 'G4', duration: '4n', velocity: 0.15 },
];

export class BackgroundMusicPlayer {
  private bassSynth?: Tone.PolySynth;
  private padSynth?: Tone.PolySynth;
  private melodySynth?: Tone.PolySynth;
  private arpSynth?: Tone.PolySynth;
  private kickSynth?: Tone.MembraneSynth;
  private snareSynth?: Tone.NoiseSynth;
  private hihatSynth?: Tone.NoiseSynth;
  private tomSynth?: Tone.MembraneSynth;

  private musicVolume?: Tone.Volume;
  private drumVolume?: Tone.Volume;
  private musicReverb?: Tone.Reverb;
  private padReverb?: Tone.Reverb;
  private musicDelay?: Tone.FeedbackDelay;
  private musicFilter?: Tone.Filter;
  private musicChorus?: Tone.Chorus;

  private bassPart?: Tone.Part;
  private padPart?: Tone.Part;
  private melodyPart?: Tone.Part;
  private arpPart?: Tone.Part;
  private drumPart?: Tone.Part;

  private currentTheme?: MusicThemeId;
  private initialized = false;
  private enabled = true;
  private volume = 0.5;
  private masterVolumeNode?: Tone.Volume;
  private isFading = false;
  private pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

  constructor() {
    this.enabled = localStorage.getItem('musicEnabled') !== 'false';
    this.volume = parseFloat(localStorage.getItem('musicVolume') || '0.5');
  }

  initialize(masterVolume: Tone.Volume): void {
    if (this.initialized) return;
    this.masterVolumeNode = masterVolume;

    // Music-specific effects chain: bright and punchy, minimal wash.
    // Higher filter frequency for clarity, short reverb for space without mud.
    this.musicVolume = new Tone.Volume(
      this.enabled ? Tone.gainToDb(this.volume) : -Infinity
    ).connect(masterVolume);
    this.musicReverb = new Tone.Reverb({ decay: 2.5, wet: 0.18 }).connect(this.musicVolume);
    this.musicDelay = new Tone.FeedbackDelay({
      delayTime: '8n.',
      feedback: 0.08,
      wet: 0.06,
    }).connect(this.musicReverb);
    this.musicFilter = new Tone.Filter({ frequency: 4000, type: 'lowpass' }).connect(
      this.musicDelay
    );
    this.musicChorus = new Tone.Chorus({ frequency: 0.3, depth: 0.08, wet: 0.08 }).connect(
      this.musicFilter
    );

    // Dedicated pad reverb -- moderate wash, not overpowering
    this.padReverb = new Tone.Reverb({ decay: 3, wet: 0.28 }).connect(this.musicVolume);

    // Dedicated synths -- separate from SFX to avoid voice stealing.
    // Each voice has a DISTINCT timbre so they complement, not blend:
    // Bass: sine (clean sub, stays out of the way)
    // Pad: fatsawtooth (stereo width, harmonic bed)
    // Melody: square (retro/bright, cuts through clearly)
    // Arp: fmsine (bell-like sparkle, high register)

    // Bass: sine sub -- clean fundamental, punchy attack
    this.bassSynth = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'sine' },
      envelope: { attack: 0.02, decay: 0.6, sustain: 0.5, release: 1.5 },
      volume: -14,
    }).connect(this.musicChorus);

    // Pad: fatsawtooth -- stereo width, moderate attack for gentle swell
    this.padSynth = new Tone.PolySynth(Tone.Synth, {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      oscillator: { type: 'fatsawtooth', spread: 10, count: 2 } as any,
      envelope: { attack: 0.8, decay: 1.2, sustain: 0.5, release: 2.5 },
      volume: -20,
    }).connect(this.padReverb);

    // Melody: square -- retro, bright, clearly distinct from bass/pad
    this.melodySynth = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'square' },
      envelope: { attack: 0.01, decay: 0.3, sustain: 0.25, release: 0.8 },
      volume: -18,
    }).connect(this.musicChorus);

    // Arp: fmsine -- bell-like, crystalline sparkle
    this.arpSynth = new Tone.PolySynth(Tone.Synth, {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      oscillator: { type: 'fmsine', modulationIndex: 3 } as any,
      envelope: { attack: 0.01, decay: 0.25, sustain: 0.1, release: 0.6 },
      volume: -16,
    }).connect(this.musicChorus);

    // Cap voices to prevent buildup
    this.bassSynth.maxPolyphony = 4;
    this.padSynth.maxPolyphony = 8;
    this.melodySynth.maxPolyphony = 6;
    this.arpSynth.maxPolyphony = 6;

    // Drum bus: dry, punchy -- minimal reverb, no delay/chorus
    this.drumVolume = new Tone.Volume(-8).connect(masterVolume);

    // Kick: membrane synth -- punchy, short decay
    this.kickSynth = new Tone.MembraneSynth({
      pitchDecay: 0.05,
      octaves: 6,
      oscillator: { type: 'sine' },
      envelope: { attack: 0.001, decay: 0.3, sustain: 0, release: 0.1 },
      volume: -6,
    }).connect(this.drumVolume);

    // Snare: filtered noise -- short burst
    this.snareSynth = new Tone.NoiseSynth({
      noise: { type: 'white' },
      envelope: { attack: 0.001, decay: 0.15, sustain: 0, release: 0.05 },
      volume: -14,
    }).connect(this.drumVolume);

    // Hi-hat: high-passed noise -- very short, crispy
    const hihatFilter = new Tone.Filter({ frequency: 8000, type: 'highpass' }).connect(this.drumVolume);
    this.hihatSynth = new Tone.NoiseSynth({
      noise: { type: 'white' },
      envelope: { attack: 0.001, decay: 0.04, sustain: 0, release: 0.01 },
      volume: -18,
    }).connect(hihatFilter);

    // Tom: membrane synth -- pitched, slightly longer than kick
    this.tomSynth = new Tone.MembraneSynth({
      pitchDecay: 0.08,
      octaves: 4,
      oscillator: { type: 'sine' },
      envelope: { attack: 0.001, decay: 0.25, sustain: 0, release: 0.1 },
      volume: -10,
    }).connect(this.drumVolume);

    this.initialized = true;
    logger.info('sound', 'BackgroundMusicPlayer initialized');
  }

  async startMusic(themeId: MusicThemeId): Promise<void> {
    if (!this.initialized || !this.enabled) return;
    if (this.currentTheme === themeId) return;

    const theme = MUSIC_THEMES[themeId];
    if (!theme) return;

    if (this.currentTheme && !this.isFading) {
      // Crossfade from current to new theme
      this.isFading = true;

      // Play transition stinger
      this.playTransitionStinger();

      // Ramp effects toward the new theme during crossfade
      this.rampEffectsForCrossfade(theme);

      this.fadeOut(FADE_DURATION);

      // Wait for partial fade, then start new theme
      await this.waitTracked(CROSSFADE_OVERLAP * 1000);
      // Release old voices before disposing parts to prevent stuck notes
      this.releaseAllVoices();
      this.disposeParts();
      this.currentTheme = themeId;
      Tone.Transport.bpm.value = theme.tempo;
      this.applySynthOverrides(theme);
      this.applyEffectsOverrides(theme);
      this.createParts(theme);
      this.startParts(theme.loopBars);
      this.fadeIn(FADE_DURATION);
      this.isFading = false;
    } else if (!this.isFading) {
      // Fresh start
      this.disposeParts();
      this.currentTheme = themeId;
      Tone.Transport.bpm.value = theme.tempo;
      this.applySynthOverrides(theme);
      this.applyEffectsOverrides(theme);
      this.createParts(theme);
      this.startParts(theme.loopBars);
      this.fadeIn(FADE_DURATION);

      if (Tone.Transport.state !== 'started') {
        Tone.Transport.start();
      }
    }

    logger.info('sound', `Started music theme: ${themeId}`, { tempo: theme.tempo });
  }

  stopMusic(): void {
    if (!this.initialized) return;

    if (this.currentTheme) {
      // Clear immediately so startMusic can restart the same theme
      this.currentTheme = undefined;
      this.isFading = false;
      // Cancel any pending crossfade timeouts
      this.clearPendingTimeouts();
      this.fadeOut(1);
      // Dispose parts after fade completes
      const id = setTimeout(() => {
        this.disposeParts();
        this.releaseAllVoices();
      }, 1100);
      this.pendingTimeouts.push(id);
    }

    logger.info('sound', 'Stopping music');
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    localStorage.setItem('musicEnabled', this.enabled.toString());

    if (!this.enabled) {
      this.clearPendingTimeouts();
      this.releaseAllVoices();
      if (this.musicVolume) {
        this.musicVolume.volume.value = -Infinity;
      }
      this.stopMusic();
    } else if (this.musicVolume) {
      this.musicVolume.volume.value = Tone.gainToDb(this.volume);
    }

    logger.info('sound', `Music ${enabled ? 'enabled' : 'disabled'}`);
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setVolume(vol: number): void {
    this.volume = Math.max(0, Math.min(1, vol));
    localStorage.setItem('musicVolume', this.volume.toString());

    if (this.musicVolume && this.enabled) {
      this.musicVolume.volume.value = Tone.gainToDb(this.volume);
    }

    logger.info('sound', `Music volume set to ${this.volume}`);
  }

  getVolume(): number {
    return this.volume;
  }

  getCurrentTheme(): MusicThemeId | undefined {
    return this.currentTheme;
  }

  dispose(): void {
    this.clearPendingTimeouts();
    this.releaseAllVoices();
    this.disposeParts();
    this.disposeSynths();
    this.disposeEffects();
    this.initialized = false;
    this.currentTheme = undefined;
    this.isFading = false;
    logger.info('sound', 'BackgroundMusicPlayer disposed');
  }

  // -- Private helpers ------------------------------------------------------

  private applySynthOverrides(_theme: WorldTheme): void {
    // Base synth timbres (triangle, fatsawtooth, fmsine) are shared
    // across all worlds. Per-world tonal differences are handled by
    // effectsOverrides (filter, reverb, delay). Kept for future extensibility.
  }

  private applyEffectsOverrides(theme: WorldTheme): void {
    if (!theme.effectsOverrides) return;

    const overrides = theme.effectsOverrides;

    if (overrides.filterFrequency !== undefined && this.musicFilter) {
      this.musicFilter.frequency.value = overrides.filterFrequency;
    }

    if (this.musicReverb) {
      if (overrides.reverbDecay !== undefined) {
        this.musicReverb.decay = overrides.reverbDecay;
      }
      if (overrides.reverbWet !== undefined) {
        this.musicReverb.wet.value = overrides.reverbWet;
      }
    }

    if (this.musicDelay) {
      if (overrides.delayFeedback !== undefined) {
        this.musicDelay.feedback.value = overrides.delayFeedback;
      }
      if (overrides.delayWet !== undefined) {
        this.musicDelay.wet.value = overrides.delayWet;
      }
    }
  }

  private rampEffectsForCrossfade(nextTheme: WorldTheme): void {
    const rampTime = CROSSFADE_OVERLAP;
    const overrides = nextTheme.effectsOverrides;

    // Ramp filter frequency toward the next theme's value
    if (overrides?.filterFrequency !== undefined && this.musicFilter) {
      this.musicFilter.frequency.rampTo(overrides.filterFrequency, rampTime);
    }

    // Ramp reverb wet toward the next theme
    if (overrides?.reverbWet !== undefined && this.musicReverb) {
      this.musicReverb.wet.rampTo(overrides.reverbWet, rampTime);
    }
  }

  private playTransitionStinger(): void {
    // Use the melody synth for the stinger since it has the most presence
    if (!this.melodySynth) return;

    const now = Tone.now();
    for (const note of TRANSITION_STINGER) {
      // Parse time offset from the note's time string
      const parts = note.time.split(':').map(Number);
      const offset = (parts[0] ?? 0) * (4 * 60 / Tone.Transport.bpm.value) +
        (parts[1] ?? 0) * (60 / Tone.Transport.bpm.value) +
        (parts[2] ?? 0) * (60 / Tone.Transport.bpm.value / 4);

      this.melodySynth.triggerAttackRelease(
        note.pitch,
        note.duration,
        now + offset,
        note.velocity
      );
    }
  }

  private createParts(theme: WorldTheme): void {
    // Wrap each Part callback in try/catch so a single synth error
    // doesn't crash the entire music loop
    if (theme.bass.length > 0) {
      this.bassPart = new Tone.Part((time, note: MusicNote) => {
        try { this.bassSynth?.triggerAttackRelease(note.pitch, note.duration, time, note.velocity); }
        catch { /* swallow - synth may be disposed mid-loop */ }
      }, theme.bass);
    }

    if (theme.pad.length > 0) {
      this.padPart = new Tone.Part((time, note: MusicNote) => {
        try { this.padSynth?.triggerAttackRelease(note.pitch, note.duration, time, note.velocity); }
        catch { /* swallow */ }
      }, theme.pad);
    }

    if (theme.melody.length > 0) {
      this.melodyPart = new Tone.Part((time, note: MusicNote) => {
        try { this.melodySynth?.triggerAttackRelease(note.pitch, note.duration, time, note.velocity); }
        catch { /* swallow */ }
      }, theme.melody);
    }

    if (theme.arp.length > 0) {
      this.arpPart = new Tone.Part((time, note: MusicNote) => {
        try { this.arpSynth?.triggerAttackRelease(note.pitch, note.duration, time, note.velocity); }
        catch { /* swallow */ }
      }, theme.arp);
    }

    if (theme.drums && theme.drums.length > 0) {
      this.drumPart = new Tone.Part((time, hit: DrumHit) => {
        try {
          switch (hit.drum) {
            case 'kick':
              this.kickSynth?.triggerAttackRelease(hit.pitch ?? 'C1', '8n', time, hit.velocity);
              break;
            case 'snare':
              this.snareSynth?.triggerAttackRelease('16n', time, hit.velocity);
              break;
            case 'hihat':
              this.hihatSynth?.triggerAttackRelease('32n', time, hit.velocity);
              break;
            case 'tom':
              this.tomSynth?.triggerAttackRelease(hit.pitch ?? 'G2', '8n', time, hit.velocity);
              break;
          }
        } catch { /* swallow */ }
      }, theme.drums);
    }
  }

  private startParts(loopBars: number): void {
    const loopEnd = `${loopBars}m`;

    [this.bassPart, this.padPart, this.melodyPart, this.arpPart, this.drumPart].forEach((part) => {
      if (part) {
        part.loop = true;
        part.loopEnd = loopEnd;
        part.start(0);
      }
    });
  }

  private disposeParts(): void {
    [this.bassPart, this.padPart, this.melodyPart, this.arpPart, this.drumPart].forEach((part) => {
      if (part) {
        part.stop();
        part.dispose();
      }
    });
    this.bassPart = undefined;
    this.padPart = undefined;
    this.melodyPart = undefined;
    this.arpPart = undefined;
    this.drumPart = undefined;
  }

  private disposeSynths(): void {
    [this.bassSynth, this.padSynth, this.melodySynth, this.arpSynth].forEach((synth) => {
      synth?.dispose();
    });
    this.bassSynth = undefined;
    this.padSynth = undefined;
    this.melodySynth = undefined;
    this.arpSynth = undefined;
    this.kickSynth?.dispose();
    this.snareSynth?.dispose();
    this.hihatSynth?.dispose();
    this.tomSynth?.dispose();
    this.kickSynth = undefined;
    this.snareSynth = undefined;
    this.hihatSynth = undefined;
    this.tomSynth = undefined;
  }

  private disposeEffects(): void {
    this.musicChorus?.dispose();
    this.musicFilter?.dispose();
    this.musicDelay?.dispose();
    this.musicReverb?.dispose();
    this.padReverb?.dispose();
    this.musicVolume?.dispose();
    this.drumVolume?.dispose();
    this.musicChorus = undefined;
    this.musicFilter = undefined;
    this.musicDelay = undefined;
    this.musicReverb = undefined;
    this.padReverb = undefined;
    this.musicVolume = undefined;
    this.drumVolume = undefined;
  }

  private fadeIn(duration: number): void {
    if (!this.musicVolume || !this.enabled) return;
    const targetDb = Tone.gainToDb(this.volume);
    this.musicVolume.volume.value = -Infinity;
    this.musicVolume.volume.rampTo(targetDb, duration);
  }

  private fadeOut(duration: number): void {
    if (!this.musicVolume) return;
    this.musicVolume.volume.rampTo(-60, duration);
  }

  private waitTracked(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const id = setTimeout(() => {
        const idx = this.pendingTimeouts.indexOf(id);
        if (idx !== -1) this.pendingTimeouts.splice(idx, 1);
        resolve();
      }, ms);
      this.pendingTimeouts.push(id);
    });
  }

  private clearPendingTimeouts(): void {
    for (const id of this.pendingTimeouts) clearTimeout(id);
    this.pendingTimeouts.length = 0;
  }

  private releaseAllVoices(): void {
    for (const synth of [this.bassSynth, this.padSynth, this.melodySynth, this.arpSynth]) {
      try { synth?.releaseAll(); } catch { /* ignore - synth may be disposed */ }
    }
  }
}
